/* Serial Seal — LOST & FOUND CONTACT LABEL renderer (window.LT).
 *
 * A peel-and-stick contact label printed on the SAME holographic vinyl and the
 * SAME presses as everything else the shop makes. Two dies, both landscape
 * cells:
 *     standard_177x075  44.958 x 19.05 mm   1062 x 450 px @600dpi
 *     large_250x100     63.5   x 25.4  mm   1500 x 600 px @600dpi
 * The numbers below ARE the dies (they are shared by value with ss-seal.js
 * SIZES and render_seals.SEAL_SIZES) and may never be changed here alone.
 *
 * WHAT THIS PRODUCT IS. A printed contact label. It carries the details the
 * customer types and nothing else. There is no serial, no registry, no verify
 * page and no lookup: a QR here opens the customer's OWN phone, inbox, profile
 * or web address directly on the finder's phone, and nothing is routed through
 * us. Nothing in this file may print, imply or depend on any of those things.
 *
 * SELF-CONTAINED ON PURPOSE — exactly like gt-tag.js, and for the same reason.
 * It renders in three places with three different sets of neighbours: the
 * builder page, the cart, and Label Farm's headless sidecar (where nothing else
 * is loaded). So it carries its own esc/fit/foil/QR code and only borrows
 * window.SS.inkSafe when that happens to exist. It NEVER reads window.st — the
 * die is passed in explicitly by every caller (a blank die silently inherited
 * from a builder's page state is how a large label gets printed as a squashed
 * standard cell).
 *
 * HOLO FIRST (owner, 2026-09-04: "too much black — we use holo materials,
 * which are the star"). Every layout is composed for bare stock — thin ink
 * rules instead of slabs, a wash instead of a plate, bare-foil windows where a
 * band would otherwise seal the label — and 'black'/'premium' are the last two
 * options in the picker rather than the first. LT.registerBgArt() is how a
 * designed set of grounds is added without editing any of this.
 *
 * ARTWORK FIRST (owner, 2026-09-05: "the bgs suck compared to the Serial Seal
 * defaults"). The picker now opens on the SAME twenty-eight artworks the seal
 * builder sells: an ART GROUND bleeds its raster to the die and lays a panel
 * of BARE FOIL under the type, which is the move ss-seal.js makes over a Seal
 * Studio background. The drawn pack at the foot of this file stays registered
 * and keeps its keys — it is demoted in the picker, never removed. Both rules
 * hold at once: an art ground prints no white ink either, its plate IS the
 * stock, and its type is ink.
 *
 * THE PICTURE IS THE STAR (same day, second pass). Getting the artwork in was
 * not enough: this product's type fills its dies, so with a code on, the plate
 * covered 82-85% of a callout and twenty-eight designed artworks all printed
 * as the same 1 mm rim. Two changes, and they work together:
 *   · THE PLATE IS SCREENED, not blank. plateBox lays the foil panel and then
 *     draws the SAME raster over it, clipped to the panel and knocked back to
 *     ART_SCREEN, so the picture runs THROUGH the type instead of stopping at
 *     it — full strength outside, 30% inside. Still no white ink anywhere: the
 *     panel is unprinted stock and the screen is lighter CMYK on top of it.
 *   · TWO ART-FIRST STYLES lead the picker, one per die. 'photo' and
 *     'photo_lg' drop the header, the email, the social handle and the address
 *     — the four things that turn a sticker into a form — and spend what they
 *     save on the picture: a compact panel carrying the name and the number,
 *     the code on a small panel in the corner, the reward as one thin band on
 *     the panel's own bottom edge. 44% of the label against the callout's 85%.
 * A design that names no style opens on the art-first one when its ground is
 * artwork, and on the type-first one everywhere else. A style the customer
 * picked is never overridden.
 *
 * HOUSE MATERIAL RULES (same as ss-seal.js / gt-tag.js):
 *   · there is no white ink. '#ffffff' is a BARE-FOIL declaration — the
 *     brightest thing this product can produce is unprinted stock. Preview
 *     mounts swap it for an iridescent sweep (foilify); print mounts never do.
 *   · pale ink vanishes into the foil, so every ground is deep and every
 *     customer accent passes through inkSafe().
 *   · the QR patch is bare foil with ink modules — maximum scan contrast on a
 *     material that has no white.
 *
 * PRINT-SAFE GEOMETRY. 1.0 mm hard margin inside the cut on every die: ink may
 * touch it, never cross it. Every text run carries data-fit; runs whose cap is
 * the distance to a die edge also carry data-fit-hard, so the fit pass spends
 * no tolerance on them. Nothing prints below MIN_TEXT (0.95 mm).
 */
(function () {
  'use strict';

  /* ── the dies ─────────────────────────────────────────────────────────
     Mirror of ss-seal.js:26-45 and render_seals.SEAL_SIZES. 'tag' and 'mm'
     exist so the sidecar and Label Farm can key off the same table:
     mm is LF's routing string (the rounded '45x19.05' sentinel is deliberate)
     and tag is the cache-uid suffix ('' for standard keeps every historical id
     byte-identical). layoutsReady is the gate — a die with no hand-composed
     layouts is never served, never auto-scaled. */
  var SIZES = {
    standard_177x075: {
      id: 'standard_177x075', displayName: 'Standard', shortName: 'Standard',
      longIn: 1.77, shortIn: 0.75, longMm: 44.958, shortMm: 19.05,
      pxLong: 1062, pxShort: 450, dpi: 600,
      mm: '45x19.05', tag: '', wire: 'std',
      M: 1.2, micro: 0.95, corner: 1.6,
      label: 'Standard 0.75″ × 1.77″',
      layoutsReady: true, isDefault: true
    },
    large_250x100: {
      id: 'large_250x100', displayName: 'Large', shortName: 'Large',
      longIn: 2.5, shortIn: 1.0, longMm: 63.5, shortMm: 25.4,
      pxLong: 1500, pxShort: 600, dpi: 600,
      mm: '63.5x25.4', tag: 'lg', wire: 'lg',
      M: 1.4, micro: 1.05, corner: 2.0,
      label: 'Large 2.5″ × 1″',
      layoutsReady: true, isDefault: false
    }
  };
  function sizeOf(id) {
    var k = String(id == null ? '' : id);
    if (k === 'lg' || k === 'large') k = 'large_250x100';
    if (k === 'std' || k === 'standard') k = 'standard_177x075';
    var z = SIZES[k];
    return (z && z.layoutsReady) ? z : SIZES.standard_177x075;
  }

  var INK = '#04050a';        /* the one ink — same as ss-seal.js */
  var FOIL = '#ffffff';       /* BARE FOIL. never white ink. */
  var MIN_TEXT = 0.95;        /* readability floor in mm — one number, one place */
  var SAFE = 1.0;             /* hard print-safe margin in mm — ink may not cross */
  var HAIR = 0.13;            /* the one hairline weight */
  var PLATE_R = 0.7;          /* the foil plate's corner radius in mm */
  /* THE SCREEN. How much of the artwork prints back INSIDE a foil plate, so
     the picture runs THROUGH the panel instead of stopping dead at its edge.
     It is the same raster laid over the plate at 30% — the plate underneath is
     still unprinted stock, so this is lighter CMYK on foil and assumes no
     white ink. 0.30 is measured, not guessed: over the darkest artwork in the
     catalogue it leaves the panel at ~70% of the stock's brightness, which
     holds ink type at a Michelson contrast of ~0.94. The harness measures that
     on all 28 artworks and enforces a floor of 0.45. See screenArt(). */
  var ART_SCREEN = 0.30;
  var DEFAULT_HDR = 'IF FOUND, PLEASE CONTACT';
  var DEFAULT_REWARD = 'REWARD IF FOUND';
  var _n = 0;
  var _dropped = [];          /* fields a layout had no room for, per svg() */
  var _plateN = 0;            /* clip ids minted for screened plates, per svg() */

  /* Per-die QR byte budget (spec §4). The builder enforces it and offers
     "switch to Large"; the renderer publishes the numbers so there is exactly
     one copy of them. (The copy spec's own table computes 44/64 at EC-H; the
     settled spec sets 32/64, which is the tighter, safer pair — and this family
     encodes at EC-M, where 32 bytes is a version 3, 29-module code.) */
  var QR_BUDGET = { standard_177x075: 32, large_250x100: 64 };

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function r2(n) { return Math.round(n * 100) / 100; }
  function hex(c, fallback) {
    c = String(c || '').trim();
    return /^#[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(c) ? c : (fallback || '#7c4dff');
  }
  /* holo-safe ink clamp — a private copy of SS.inkSafe so the sidecar (which
     loads nothing else) clamps identically to the storefront. */
  function inkSafe(c) {
    if (window.SS && window.SS.inkSafe) { try { return window.SS.inkSafe(c); } catch (_) {} }
    var h6 = hex(c);
    if (h6.length === 4) h6 = '#' + h6[1] + h6[1] + h6[2] + h6[2] + h6[3] + h6[3];
    var r = parseInt(h6.slice(1, 3), 16) / 255,
        g = parseInt(h6.slice(3, 5), 16) / 255,
        b = parseInt(h6.slice(5, 7), 16) / 255;
    var mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2;
    if (l <= 0.45) return h6;
    var d = mx - mn, s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1)), hu = 0;
    if (d !== 0) {
      if (mx === r) hu = ((g - b) / d) % 6;
      else if (mx === g) hu = (b - r) / d + 2;
      else hu = (r - g) / d + 4;
      hu = (hu * 60 + 360) % 360;
    }
    var L = 0.40, S = s < 0.15 ? s : Math.max(s, 0.55);
    var C = (1 - Math.abs(2 * L - 1)) * S,
        X = C * (1 - Math.abs(((hu / 60) % 2) - 1)),
        m = L - C / 2, rr = 0, gg = 0, bb = 0;
    if (hu < 60) { rr = C; gg = X; } else if (hu < 120) { rr = X; gg = C; }
    else if (hu < 180) { gg = C; bb = X; } else if (hu < 240) { gg = X; bb = C; }
    else if (hu < 300) { rr = X; bb = C; } else { rr = C; bb = X; }
    function h2(v) { v = Math.round((v + m) * 255); return (v < 16 ? '0' : '') + v.toString(16); }
    return '#' + h2(rr) + h2(gg) + h2(bb);
  }

  /* cut-corner rect — the ss-seal panel language, which is OUR furniture */
  function cutRect(x, y, w, h, c) {
    return 'M' + r2(x + c) + ' ' + r2(y) + 'H' + r2(x + w - c) +
      'L' + r2(x + w) + ' ' + r2(y + c) + 'V' + r2(y + h - c) +
      'L' + r2(x + w - c) + ' ' + r2(y + h) + 'H' + r2(x + c) +
      'L' + r2(x) + ' ' + r2(y + h - c) + 'V' + r2(y + c) + 'Z';
  }
  function roundRect(x, y, w, h, r) {
    return 'M' + r2(x + r) + ' ' + r2(y) + 'H' + r2(x + w - r) +
      'Q' + r2(x + w) + ' ' + r2(y) + ' ' + r2(x + w) + ' ' + r2(y + r) +
      'V' + r2(y + h - r) + 'Q' + r2(x + w) + ' ' + r2(y + h) + ' ' + r2(x + w - r) + ' ' + r2(y + h) +
      'H' + r2(x + r) + 'Q' + r2(x) + ' ' + r2(y + h) + ' ' + r2(x) + ' ' + r2(y + h - r) +
      'V' + r2(y + r) + 'Q' + r2(x) + ' ' + r2(y) + ' ' + r2(x + r) + ' ' + r2(y) + 'Z';
  }

  /* Halo colour, set per-render in svg(). ALWAYS the opposite polarity to the
     glyph, so type separates from whatever ground is under it.
     _HALO_W is its weight as a fraction of the type size and _HALO_SO its
     opacity: on a translucent TINT the halo is bare foil and therefore VISIBLE
     (on open stock it lands on foil and disappears, on ink it is the whole
     point). A full-weight foil outline round every detail row on a tint reads
     as chalk, so the tint grounds ask for a lighter, softer one. */
  var _HALO = null, _HALO_W = 0.15, _HALO_SO = 0;

  function txt(x, y, size, fill, s, opts) {
    opts = opts || {};
    if (s == null || s === '') return '';
    var halo = (opts.halo === false || !_HALO || fill === _HALO) ? '' :
      ' paint-order="stroke fill" stroke="' + _HALO + '" stroke-width="' +
      r2(Math.max(0.10, size * _HALO_W)) + '" stroke-linejoin="round"' +
      (_HALO_SO ? ' stroke-opacity="' + _HALO_SO + '"' : '');
    return '<text x="' + r2(x) + '" y="' + r2(y) + '" font-family="Helvetica, Arial, sans-serif"' +
      halo +
      ' font-size="' + r2(size) + '"' +
      (opts.w === false ? '' : ' font-weight="bold"') +
      (opts.anchor ? ' text-anchor="' + opts.anchor + '"' : '') +
      (opts.ls ? ' letter-spacing="' + opts.ls + '"' : '') +
      (opts.op ? ' opacity="' + opts.op + '"' : '') +
      (opts.fit ? ' data-fit="' + r2(opts.fit) + '"' : '') +
      (opts.fit && opts.hard !== false ? ' data-fit-hard="1"' : '') +
      (opts.role ? ' data-lt-role="' + opts.role + '"' : '') +
      ' fill="' + fill + '">' + esc(s) + '</text>';
  }
  function line(o, x1, y1, x2, y2, col, op, w) {
    o.push('<path d="M' + r2(x1) + ' ' + r2(y1) + 'L' + r2(x2) + ' ' + r2(y2) +
      '" stroke="' + col + '" stroke-width="' + (w || HAIR) + '"' +
      (op ? ' opacity="' + op + '"' : '') + '/>');
  }
  /* the small tracked label voice (QR captions) — never below MIN_TEXT */
  function micro(o, k, x, y, s, col, opts) {
    opts = opts || {};
    o.push(txt(x, y, Math.max(MIN_TEXT, k.SZ.micro), col, String(s).toUpperCase(),
      { ls: 0.2, op: opts.op || 0.6, anchor: opts.anchor, fit: opts.fit, hard: true,
        role: opts.role }));
  }
  /* the logo slot: contain-fit image on a quiet backing so any mark reads on
     any ground. Any ground carrying ink — a flood OR a translucent tint — gets
     a soft bare-foil patch under the mark; open stock is already the brightest
     thing on the label and gets nothing. */
  function logoBox(c, k, x, y, w, h) {
    var o = [];
    if (k.grounded) {
      o.push('<path d="' + cutRect(x - 0.3, y - 0.3, w + 0.6, h + 0.6, 0.6) +
        '" fill="url(#' + k.id + 'f)" stroke="' + INK +
        '" stroke-width="0.12" stroke-opacity="0.5"/>');
    }
    o.push('<image href="' + esc(c.logo) + '" x="' + r2(x) + '" y="' + r2(y) +
      '" width="' + r2(w) + '" height="' + r2(h) + '" preserveAspectRatio="xMidYMid meet"/>');
    return o.join('');
  }

  /* ── BACKGROUND STYLES — HOLO FIRST ───────────────────────────────────
     The stock is the product. This shop prints CMYK on holographic vinyl, so
     the brightest, most expensive-looking thing a label can do is show the
     material: bare foil with ink laid over it. A flood of ink HIDES the stock,
     and hiding the stock is the one thing this set must not do by default
     (owner, 2026-09-04: "too much black — we use holo materials, which are the
     star").

     Hence the order these were composed in. It is no longer the head of the
     picker — the artwork catalogue below now leads it (see ART) — but every
     rule here still binds, and the bare stock still comes before any ink:
       1-3  bare foil        holo (no ink at all), silver, prismatic
       4-8  translucent ink  a 0.44-0.50 screen of one deep colour. The rainbow
                             shifts straight through a screen that light, so the
                             plate still reads as foil, tinted — and TYPE STAYS
                             INK (dark:false). Ink on a 0.50 navy/oxblood/forest
                             screen over foil measures ~5.5:1 at these sizes,
                             more contrast than knockout type gets on the same
                             ground, and it needs no halo to hold it.
       9-10 opaque ink       black and premium, kept as options at the END.

     dark:  does TEXT knock out to bare foil ('#ffffff') or print as ink.
     tint:  a deep hue laid as a SCREEN, never a flood.
     op:    that screen's ink percentage (0.35-0.55 — above that the foil dies).
     art:   optional per-ground furniture, registered by LT.registerBgArt().
     kind:  'art' marks an IMAGE ground; it carries image/plate/accent instead
            of an art() function and svg() draws it as a full-bleed raster. */
  var BGS = [
    { key: 'holo',      name: 'Open Holo', dark: false },
    { key: 'silver',    name: 'Silver',    dark: false },
    { key: 'prismatic', name: 'Prismatic', dark: false },
    { key: 'blue',      name: 'Navy',      dark: false, tint: '#123a6e', op: 0.50 },
    { key: 'red',       name: 'Oxblood',   dark: false, tint: '#6e0f1e', op: 0.50 },
    { key: 'green',     name: 'Forest',    dark: false, tint: '#0e4f2c', op: 0.50 },
    { key: 'purple',    name: 'Violet',    dark: false, tint: '#40196b', op: 0.46 },
    { key: 'gold',      name: 'Bronze',    dark: false, tint: '#6b4a00', op: 0.44 },
    { key: 'black',     name: 'Black',     dark: true },
    { key: 'premium',   name: 'Premium',   dark: true }
  ];
  /* ── ART GROUNDS — the Serial Seal artwork catalogue ──────────────────
     Owner, 2026-09-05: "the bgs suck compared to the Serial Seal defaults."
     The drawn pack above stays (its keys are forever, and a design record
     stores only bg:'<key>') but it is DEMOTED: the grounds a customer sees
     first are the SAME twenty-eight artworks the seal builder sells.

     All of it is in-house work — every one of these was generated for this
     shop and is reused here by the owner's instruction — so nothing is
     licensed, credited or rate-limited.

     THE TABLE IS THE CATALOGUE. window.SS_DESIGNS is a storefront object and
     the press sidecar (tools/render_lost.py) does not load the seal builder,
     so reading it at runtime would give the press a different set of grounds
     from the preview. Instead the catalogue is BAKED here, one row per design,
     and the pinned renderer carries it to the press byte for byte. A row is
        [ key, name, <bg_ls filename with its ?v=>, defaults.color ]
     where the key is the SEAL key: the ground's key is 'd_' + it, so a Lost &
     Found design record can never collide with a drawn-pack key.

     bg_ls is the LANDSCAPE crop (44.958 x 19.05 = 2.36:1), which is this
     product's standard die. The large die (63.5 x 25.4 = 2.5:1) is close
     enough that the same file cover-crops to it with 2.7% off the long edge,
     which is what preserveAspectRatio="xMidYMid slice" does — the seal
     renderer takes the identical shortcut (ss-seal.js:1866).

     The ?v= in each URL is the asset's own cache key and is kept VERBATIM: a
     stripped or re-stamped version string is a different file to the CDN and
     a cache miss on the press. bg_pf / bg_lf (the fold crops) have no meaning
     for a flat contact label and are not carried.

     `accent` is the design's own suggested colour, exactly as the catalogue
     declares it — a hint for the builder's picker, NOT an override. The
     renderer keeps printing the customer's own accent (norm() has already run
     it through inkSafe); a few of these hints are pale on purpose and would
     be clamped the moment anything used them as ink. An empty string means
     the catalogue offers no hint for that design. */
  var ART_CDN = 'https://cdn.shopify.com/s/files/1/0721/3652/3850/t/1/assets/';
  var ART = [
    ['p_orbit_92ec',           'Orbit',   'seal-bg-p_orbit_92ec-ls.webp?v=1788129802',           '#5b21b6'],
    ['p_nova_b324',            'Nova',    'seal-bg-p_nova_b324-ls.webp?v=1788129794',            '#5b21b6'],
    ['p_fire_phoenix_a157',    'Heat',    'seal-bg-p_fire_phoenix_a157-ls.webp?v=1785722845',    '#a16207'],
    ['p_prismatic_7264',       'Mint',    'seal-bg-p_prismatic_7264-ls.webp?v=1785722446',       '#5b21b6'],
    ['p_garden_be60',          'Fabled',  'seal-bg-p_garden_be60-ls.webp?v=1785722294',          '#ddef4d'],
    ['p_holo_storm_6dc5',      'Cosmic',  'seal-bg-p_holo_storm_6dc5-ls.webp?v=1785720080',      '#5b21b6'],
    ['p_moonlit_eclipse_0336', 'Omen',    'seal-bg-p_moonlit_eclipse_0336-ls.webp?v=1785720826', '#ffffff'],
    ['p_lightning_b52d',       'Surge',   'seal-bg-p_lightning_b52d-ls.webp?v=1785720834',       '#ffdd00'],
    ['p_one_a381',             'Alpha',   'seal-bg-p_one_a381-ls.webp?v=1785720841',             '#5b21b6'],
    ['p_bubble_2ea2',          'Chase',   'seal-bg-p_bubble_2ea2-ls.webp?v=1785720849',          '#5b21b6'],
    ['p_aqua_storm_211f',      'Riptide', 'seal-bg-p_aqua_storm_211f-ls.webp?v=1785720857',      '#374151'],
    ['p_inferno_93b8',         'Burn',    'seal-bg-p_inferno_93b8-ls.webp?v=1785722084',         '#ffffff'],
    ['p_vortex_0c99',          'Vortex',  'seal-bg-p_vortex_0c99-ls.webp?v=1787037999',          ''],
    ['p_ember_d7b1',           'Ember',   'seal-bg-p_ember_d7b1-ls.webp?v=1787038007',           ''],
    ['p_frost_9372',           'Frost',   'seal-bg-p_frost_9372-ls.webp?v=1787038017',           ''],
    ['p_venom_ffe2',           'Venom',   'seal-bg-p_venom_ffe2-ls.webp?v=1787038026',           ''],
    ['p_static_34c7',          'Static',  'seal-bg-p_static_34c7-ls.webp?v=1787038034',          ''],
    ['p_halo_09f5',            'Halo',    'seal-bg-p_halo_09f5-ls.webp?v=1787038043',            ''],
    ['p_pearl_450c',           'Pearl',   'seal-bg-p_pearl_450c-ls.webp?v=1787038480',           ''],
    ['p_divine_b392',          'Divine',  'seal-bg-p_divine_b392-ls.webp?v=1787038489',          ''],
    ['p_sakura_a5f6',          'Sakura',  'seal-bg-p_sakura_a5f6-ls.webp?v=1787038498',          ''],
    ['p_mirage_c95d',          'Mirage',  'seal-bg-p_mirage_c95d-ls.webp?v=1787038507',          ''],
    ['p_flux_12e6',            'Flux',    'seal-bg-p_flux_12e6-ls.webp?v=1787332813',            ''],
    ['p_onyx_4969',            'Onyx',    'seal-bg-p_onyx_4969-ls.webp?v=1787332822',            ''],
    ['p_rift_0596',            'Rift',    'seal-bg-p_rift_0596-ls.webp?v=1787332831',            ''],
    ['p_circuit_4efe',         'Circuit', 'seal-bg-p_circuit_4efe-ls.webp?v=1787332839',         ''],
    ['p_kaleido_d392',         'Kaleido', 'seal-bg-p_kaleido_d392-ls.webp?v=1787332848',         ''],
    ['p_bloom_0bc7',           'Bloom',   'seal-bg-p_bloom_0bc7-ls.webp?v=1787332857',           '']
  ];
  /* the catalogue as BGS entries, in catalogue order (which is picker order) */
  function artGrounds() {
    var out = [];
    for (var i = 0; i < ART.length; i++) {
      var r = ART[i];
      out.push({ key: 'd_' + r[0], name: r[1], dark: false, plate: true,
                 image: /^https?:/i.test(r[2]) ? r[2] : ART_CDN + r[2],
                 accent: r[3] || '' });
    }
    return out;
  }

  /* one name, one place — norm(), bgOf() and the picker agree. The default is
     the first artwork, not the bare stock: the grounds are the product's face
     now and a customer who touches nothing gets one. */
  var DEFAULT_BG = 'd_p_orbit_92ec';
  function bgOf(k) {
    for (var i = 0; i < BGS.length; i++) if (BGS[i].key === k) return BGS[i];
    for (var j = 0; j < BGS.length; j++) if (BGS[j].key === DEFAULT_BG) return BGS[j];
    return BGS[0];
  }
  /* A ground that lays a flood or a screen. A logo needs a quiet foil backing
     on one of those and needs nothing at all on open stock. */
  function grounded(bg) { return !!(bg && (bg.dark || bg.tint)); }

  /* ── LT.registerBgArt(list) ───────────────────────────────────────────
     The one supported way to add designed grounds without touching this file.
     `list` is an array of { key, name, dark, tint, op, art } — or the whole
     module object a background pack exports ({ author, list }). Each entry's
     `art(W, H, ns, dark, accent)` returns an SVG string drawn immediately
     after the ground and before every glyph (see svg()).

     An entry may instead carry `image` (a URL) and become an ART GROUND:
     kind:'art', the raster bled to the die and a bare-foil plate under the
     type. `plate` defaults to true and only a ground that has proved it does
     not need one may pass false. `accent` rides along as the design's own
     suggested colour for the picker; the renderer never prints it.

     Rules an art function must keep, because nothing here can enforce them:
       · mint every id as ns + something. Two mounts of the same ground live
         in one document on the builder page and on the cart;
       · '#ffffff' is BARE FOIL, not white ink — preview mounts swap it for
         the iridescent sweep and the press leaves it unprinted. Every other
         fill is ink and foilify() leaves it exactly as written;
       · stay under the type. A ground that hides a phone number is a defect,
         not a style.
     Entries mutate BGS IN PLACE (a picker built from LT.BGS holds the array
     by reference), replacing an existing key or appending. Returns the count. */
  function registerBgArt(list) {
    if (!list) return 0;
    if (!list.length && list.list) list = list.list;
    if (!list || !list.length) return 0;
    var added = 0;
    for (var i = 0; i < list.length; i++) {
      var e = list[i];
      if (!e || !e.key) continue;
      var img = (typeof e.image === 'string') ? e.image.trim() : '';
      if (typeof e.art !== 'function' && !img) continue;
      var entry = { key: String(e.key), name: String(e.name || e.key),
                    dark: !!e.dark };
      if (typeof e.art === 'function') entry.art = e.art;
      if (img) {
        entry.kind = 'art';
        entry.image = img;
        /* the plate is the whole reason type survives over busy artwork, so
           it is opt-OUT and never opt-in */
        entry.plate = e.plate !== false;
        if (e.accent) entry.accent = hex(e.accent, '#7c4dff');
      }
      if (e.tint) {
        entry.tint = hex(e.tint, INK);
        entry.op = Math.max(0.05, Math.min(0.6, +e.op || 0.45));
      }
      var at = -1;
      for (var j = 0; j < BGS.length; j++) if (BGS[j].key === entry.key) { at = j; break; }
      if (at >= 0) BGS[at] = entry; else BGS.push(entry);
      added++;
    }
    return added;
  }
  /* The catalogue goes in through the same door a pack does, at parse time and
     before anything can render: bgOf() has to be able to resolve DEFAULT_BG on
     the very first svg() call. The final picker ORDER is applied once, at the
     tail of this file, after the drawn pack has registered too. */
  registerBgArt(artGrounds());

  /* ── LAYOUTS ──────────────────────────────────────────────────────────
     Five per die, hand-composed for that die (the house rule is a manual
     rebuild per size, never a scale factor — ss-seal.js:256-262).

     THE ART-FIRST STYLE LEADS (owner, 2026-09-05: the picture has to be the
     star). 'photo' / 'photo_lg' are composed around the artwork rather than
     around the type: no header, no email/social/address, a compact screened
     panel carrying the name and the number, and the code tucked into a corner
     on a small panel of its own. They are FIRST in this table, so the builder
     shows them first, and defaultLayout() opens on them whenever the ground is
     one of the twenty-eight artworks. A customer's explicit pick is never
     overridden — only the default moves.

     `photo:true` is that flag, and nothing else keys off it.

     KEEP EVERY `note` UNDER ~40 CHARACTERS. The line under the style strip is
     a FIXED one-line box on a phone (.sqk-note, 30px at 360-430px): a longer
     sentence wraps to a second line that is clipped away with no ellipsis, so
     the reader gets half a sentence and no sign there was more. Measured on
     the live builder at 360, 390 and 430px — every note here fits on one
     line at all three, and lt_builder_check.py's art_note_step asserts it.

     `qr:true` layouts REFLOW when the design has no code: the QR column's
     width returns to the text column and the extra detail rows open up. That
     reflow IS the no-QR variant of every QR layout — one key, both states, so
     a customer who turns the code off keeps the style they picked.

     `carries` is the field-fit matrix (copy spec §5e) for the builder: a field
     the layout cannot carry is greyed there, and if one arrives anyway the
     renderer reports it in `dropped` rather than silently losing it.
     `qrActions` restricts which codes a layout may carry (a layout with no
     room for a printed fallback under the patch may only carry a code whose
     destination already prints in the text column — copy spec §4d rule 6). */
  var LAYOUTS = [
    { key: 'photo', hidden: true, orient: 'h', name: 'Big Picture', size: 'standard_177x075', qr: true,
      note: 'The artwork, name and number on a panel',
      carries: ['name', 'phone', 'reward'],
      logo: false, qrCaption: true, qrActions: ['call', 'text', 'whatsapp'],
      photo: true },
    { key: 'callout', hidden: true, orient: 'h', name: 'Call Me', size: 'standard_177x075', qr: true,
      note: 'Number beside the code',
      carries: ['header', 'name', 'phone', 'email', 'social', 'reward'],
      logo: true, qrCaption: true },
    { key: 'plain', hidden: true, orient: 'h', name: 'Full Contact', size: 'standard_177x075', qr: false,
      note: 'Everything in type, no code',
      carries: ['header', 'name', 'phone', 'email', 'social', 'addr1', 'addr2', 'reward'],
      logo: true },
    { key: 'banner', hidden: true, orient: 'h', name: 'Header Band', size: 'standard_177x075', qr: true,
      note: 'Band across the top',
      carries: ['header', 'name', 'phone', 'email', 'social', 'reward'],
      logo: true, qrCaption: false, qrActions: ['call', 'text', 'whatsapp'] },
    { key: 'strip', hidden: true, orient: 'v', name: 'Portrait Strip', size: 'standard_177x075', qr: true,
      note: 'Reads down a bottle or a case edge',
      carries: ['header', 'name', 'phone', 'email', 'social', 'addr1', 'addr2', 'note', 'reward'],
      logo: false, qrCaption: true },
    { key: 'photo_lg', hidden: true, orient: 'h', name: 'Big Picture', size: 'large_250x100', qr: true,
      note: 'The artwork, name and number on a panel',
      carries: ['name', 'phone', 'reward'],
      logo: false, qrCaption: true, qrActions: ['call', 'text', 'whatsapp'],
      photo: true },
    { key: 'callout_lg', hidden: true, orient: 'h', name: 'Call Me', size: 'large_250x100', qr: true,
      note: 'The flagship — every field plus the code',
      carries: ['header', 'name', 'phone', 'email', 'social', 'addr1', 'addr2', 'note', 'reward'],
      logo: true, qrCaption: true },
    { key: 'plain_lg', hidden: true, orient: 'h', name: 'Full Contact', size: 'large_250x100', qr: false,
      note: 'The biggest number this product prints',
      carries: ['header', 'name', 'phone', 'email', 'social', 'addr1', 'addr2', 'note', 'reward'],
      logo: true },
    { key: 'banner_lg', hidden: true, orient: 'h', name: 'Header Band', size: 'large_250x100', qr: true,
      note: 'Band across the top',
      carries: ['header', 'name', 'phone', 'email', 'social', 'addr1', 'reward'],
      logo: true, qrCaption: false, qrActions: ['call', 'text', 'whatsapp'] },
    { key: 'card_lg', hidden: true, orient: 'h', name: 'Name Plate', size: 'large_250x100', qr: true,
      note: 'Name panel left, contact details right',
      carries: ['header', 'name', 'phone', 'email', 'social', 'addr1', 'addr2', 'note', 'reward'],
      logo: true, qrCaption: true },
    /* ── THE CLEAN SET (owner 2026-09-30) — what the picker shows. Same four
       styles on both dies, two across and two on end; see drawLine. ── */
    { key: 'line', name: 'Contact Card', size: 'standard_177x075', orient: 'h', qr: true,
      note: 'Name and number, code at the side',
      carries: ['header', 'name', 'phone', 'email', 'social', 'addr1', 'addr2', 'note', 'reward'],
      logo: true, qrCaption: true },
    { key: 'center', name: 'Centered', size: 'standard_177x075', orient: 'h', qr: false,
      note: 'Everything in type, centred',
      carries: ['header', 'name', 'phone', 'email', 'social', 'addr1', 'addr2', 'note', 'reward'],
      logo: true },
    { key: 'tower', name: 'Vertical', size: 'standard_177x075', orient: 'v', qr: true,
      note: 'Reads top to bottom, code first',
      carries: ['header', 'name', 'phone', 'email', 'social', 'addr1', 'addr2', 'note', 'reward'],
      logo: true, qrCaption: true },
    { key: 'tower_name', name: 'Vertical Name', size: 'standard_177x075', orient: 'v', qr: true,
      note: 'Reads top to bottom, name first',
      carries: ['header', 'name', 'phone', 'email', 'social', 'addr1', 'addr2', 'note', 'reward'],
      logo: true, qrCaption: true },
    { key: 'line_lg', name: 'Contact Card', size: 'large_250x100', orient: 'h', qr: true,
      note: 'Name and number, code at the side',
      carries: ['header', 'name', 'phone', 'email', 'social', 'addr1', 'addr2', 'note', 'reward'],
      logo: true, qrCaption: true },
    { key: 'center_lg', name: 'Centered', size: 'large_250x100', orient: 'h', qr: false,
      note: 'Everything in type, centred',
      carries: ['header', 'name', 'phone', 'email', 'social', 'addr1', 'addr2', 'note', 'reward'],
      logo: true },
    { key: 'tower_lg', name: 'Vertical', size: 'large_250x100', orient: 'v', qr: true,
      note: 'Reads top to bottom, code first',
      carries: ['header', 'name', 'phone', 'email', 'social', 'addr1', 'addr2', 'note', 'reward'],
      logo: true, qrCaption: true },
    { key: 'tower_name_lg', name: 'Vertical Name', size: 'large_250x100', orient: 'v', qr: true,
      note: 'Reads top to bottom, name first',
      carries: ['header', 'name', 'phone', 'email', 'social', 'addr1', 'addr2', 'note', 'reward'],
      logo: true, qrCaption: true }
  ];
  function layoutsFor(sizeId) {
    var sid = sizeOf(sizeId).id, out = [];
    for (var i = 0; i < LAYOUTS.length; i++) if (LAYOUTS[i].size === sid) out.push(LAYOUTS[i]);
    return out;
  }
  function layOf(key, sizeId) {
    var list = layoutsFor(sizeId);
    for (var i = 0; i < list.length; i++) if (list[i].key === key) return list[i];
    return list[0];
  }
  /* THE DEFAULT STYLE FOLLOWS THE GROUND. A design that names no layout (or
     names one this die does not have) opens on the art-first style when it is
     sitting on one of the artworks, and on the type-first one everywhere else
     — putting 'Big Picture' on bare foil would be a blank panel on a blank
     label. Only the DEFAULT moves: a layout the customer actually picked is
     resolved by key in norm() and never second-guessed here. */
  function defaultLayout(sizeId, bg) {
    var list = layoutsFor(sizeId), want = !!(bg && bg.kind === 'art');
    for (var i = 0; i < list.length; i++) if (!!list[i].photo === want) return list[i];
    return list[0];
  }

  /* ── the wire form ────────────────────────────────────────────────────
     norm() accepts BOTH shapes on purpose: the compact cart record the sidecar
     replays (la/bg/n/p/e1/m/a1/a2/r/s/x/qa/qd/qb/lo/ac/h) and the long-named
     object the builder keeps in memory. One normaliser means the cart, the
     preview and the press all read the same design the same way. */
  /* THE PICKER (owner 2026-09-30). layoutsFor() is every style a record may
     name and never shrinks; the picker shows only the current set, filtered by
     orientation. A NEW design starts on START_BG and the first picker style.
     norm()'s own defaults (DEFAULT_BG, defaultLayout) are untouched: they are
     what an OLD record with no ground or no style has always resolved to, and
     a reprint must come out the way it was sold. */
  function pickerLayouts(sizeId, orient) {
    var all = layoutsFor(sizeId), out = [];
    for (var i = 0; i < all.length; i++) {
      var l = all[i];
      if (l.hidden) continue;
      if (orient && (l.orient || 'h') !== orient) continue;
      out.push(l);
    }
    return out;
  }
  function startLayout(sizeId, orient) {
    var l = pickerLayouts(sizeId, orient);
    return l.length ? l[0].key : defaultLayout(sizeId).key;
  }
  var START_BG = 'holo';
  /* LESS INK (owner 2026-09-30: "use less ink no dark bgs"). The grounds the
     picker offers, in picker order: the eight artworks whose plate file
     averages lighter than 0.55 (Mint .83 ... Riptide .58 - the other twenty
     measure .47 down to .08), then the bare stock, then the drawn grounds that
     lay 25% ink or less on a print mount. Not offered: the twenty dark
     artworks, the five colour screens (32-42% ink), Ink Slab, Air Mail, Black,
     Premium. EVERY key stays in BGS and renders exactly as before — a design
     record is forever. Measured 2026-09-30, scratchpad ground_ink.py. */
  var PICK_GROUNDS = ['d_p_prismatic_7264', 'd_p_divine_b392', 'd_p_holo_storm_6dc5',
    'd_p_garden_be60', 'd_p_pearl_450c', 'd_p_mirage_c95d', 'd_p_sakura_a5f6',
    'd_p_aqua_storm_211f', 'holo', 'silver', 'prismatic', 'tworule', 'aperture',
    'dotgrid', 'contour', 'compass', 'hairline', 'duotone', 'ridgeline', 'textpanel',
    'anodize', 'circuit', 'meridian', 'seachart'];
  function pickerGrounds() {
    var out = [];
    for (var i = 0; i < PICK_GROUNDS.length; i++) {
      for (var j = 0; j < BGS.length; j++) {
        if (BGS[j].key === PICK_GROUNDS[i]) { out.push(BGS[j]); break; }
      }
    }
    return out;
  }

  var QR_ACTIONS = ['none', 'call', 'text', 'email', 'instagram', 'whatsapp', 'web'];
  function pick() {
    for (var i = 0; i < arguments.length; i++) {
      var v = arguments[i];
      if (v != null && v !== '') return v;
    }
    return '';
  }
  function clean(s) { return String(s == null ? '' : s).replace(/\s+/g, ' ').trim(); }

  function norm(c, sizeId) {
    c = c || {};
    var SZ = sizeOf(pick(sizeId, c.size_id, c.sizeId, c.size));
    var q = c.qr || {};
    var action = String(pick(c.qa, q.action, 'none')).toLowerCase();
    if (QR_ACTIONS.indexOf(action) < 0) action = 'none';
    /* header: absent or empty prints the default; a deliberate blank (a lone
       space) prints no header at all. */
    var rawH = (c.h != null ? c.h : c.hdr);
    var hdr;
    if (rawH == null || rawH === '') hdr = DEFAULT_HDR;
    else if (!String(rawH).trim()) hdr = '';
    else hdr = clean(rawH);
    /* reward: presence of the text IS the switch (the wire form omits it when
       the reward is off); the long form may pass rewardOn with no wording. */
    var rawR = (c.r != null ? c.r : c.reward);
    var reward = clean(rawR);
    if (!reward && c.rewardOn) reward = DEFAULT_REWARD;

    var social = clean(pick(c.s, c.social)).replace(/^@+/, '')
      .replace(/^(?:https?:\/\/)?(?:www\.)?instagram\.com\//i, '');
    /* the ground first — the default LAYOUT depends on it */
    var ground = bgOf(clean(pick(c.bg, DEFAULT_BG)));
    var rawLa = clean(pick(c.la, c.layout));
    var laid = null;
    for (var li = 0, ll = layoutsFor(SZ.id); li < ll.length; li++) {
      if (ll[li].key === rawLa) { laid = ll[li]; break; }
    }
    var out = {
      sizeId: SZ.id,
      layout: (laid || defaultLayout(SZ.id, ground)).key,
      bg: ground.key,
      hdr: hdr,
      name: clean(pick(c.n, c.name)),
      phone: clean(pick(c.p, c.phone)),
      e164: String(pick(c.e1, c.e164) || '').replace(/[^\d+]/g, ''),
      email: clean(pick(c.m, c.email)),
      addr1: clean(pick(c.a1, c.addr1)),
      addr2: clean(pick(c.a2, c.addr2)),
      reward: reward,
      social: social,
      note: clean(pick(c.x, c.note)),
      qa: action,
      qd: clean(pick(c.qd, q.dest)),
      qb: String(pick(c.qb, q.body) || '').replace(/\s+/g, ' ').trim(),
      logo: String(pick(c.lo, c.logo) || '').trim(),
      accent: inkSafe(pick(c.ac, c.accent, '#7c4dff'))
    };
    return out;
  }

  /* ── QR: the ONE printed string ───────────────────────────────────────
     Nothing outside this file may assemble a QR payload. A renderer that
     composed its own URL could not be stopped from prefixing a scheme, and the
     caption, the printed fallback and the byte budget all have to agree with
     the bytes that are actually encoded (ss-seal.js:72-95 makes the same rule
     for seals). The builder passes a structured action and this composes.

     Scheme discipline: tel:/sms:/mailto: are mandatory (a bare number is not a
     dialable URI); https:// is OMITTED for instagram/whatsapp/web because
     phone cameras prepend it and it costs 8 bytes of module density. */
  function encBody(s) {
    /* iOS's sms: handler has been observed to truncate on an unescaped
       apostrophe, and ! is not escaped by encodeURIComponent either. */
    return encodeURIComponent(String(s || '')).replace(/'/g, '%27').replace(/!/g, '%21');
  }
  function bareHost(u) {
    return String(u || '').trim()
      .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')   /* scheme off — cameras add it back */
      .replace(/\/+$/, '');
  }
  function qrPayload(cfg) {
    var c = (cfg && cfg.qa !== undefined && cfg.sizeId) ? cfg : norm(cfg);
    var digits = String(c.e164 || '').replace(/\D/g, '');
    switch (c.qa) {
      case 'call':
        return c.e164 ? 'tel:' + c.e164 : '';
      case 'text':
        /* '?&body=' — a question mark immediately followed by an ampersand is
           the one separator both Android (?body=) and iOS (&body=) accept.
           One extra byte, two working platforms. */
        return c.e164 ? ('sms:' + c.e164 + (c.qb ? '?&body=' + encBody(c.qb) : '')) : '';
      case 'email':
        return c.email ? ('mailto:' + c.email + (c.qb ? '?subject=' + encBody(c.qb) : '')) : '';
      case 'instagram':
        var h = (c.qd || c.social).replace(/^@+/, '')
          .replace(/^(?:https?:\/\/)?(?:www\.)?instagram\.com\//i, '');
        return h ? 'instagram.com/' + h : '';
      case 'whatsapp':
        return digits ? 'wa.me/' + digits : '';
      case 'web':
        var w = bareHost(c.qd);
        return w || '';
      default:
        return '';
    }
  }
  /* the caption above the patch — it must tell the truth about where THIS
     code goes, so it is derived from the action and never authored. */
  function qrCaption(cfg) {
    var c = (cfg && cfg.qa !== undefined && cfg.sizeId) ? cfg : norm(cfg);
    if (!qrPayload(c)) return '';
    return { call: 'SCAN TO CALL', text: 'SCAN TO TEXT', email: 'SCAN TO EMAIL',
             instagram: 'SCAN FOR IG', whatsapp: 'SCAN FOR WHATSAPP',
             web: 'SCAN' }[c.qa] || '';
  }
  /* the plain-text fallback printed under (or beside) every code. A finder
     with a dead phone, gloves on or no data is the most likely finder, and a
     label whose only route is a code is a blank sticker to them. */
  function qrFallback(cfg) {
    var c = (cfg && cfg.qa !== undefined && cfg.sizeId) ? cfg : norm(cfg);
    if (!qrPayload(c)) return '';
    switch (c.qa) {
      case 'call': return c.phone || c.e164;
      case 'text': return 'TEXT ' + (c.phone || c.e164);
      case 'email': return c.email;
      case 'instagram': return '@' + ((c.qd || c.social).replace(/^@+/, '')
        .replace(/^(?:https?:\/\/)?(?:www\.)?instagram\.com\//i, ''));
      case 'whatsapp': return 'WHATSAPP ' + (c.phone || c.e164);
      case 'web': return bareHost(c.qd).split('/')[0];
      default: return '';
    }
  }
  /* UTF-8 byte length — the encoder picks its version from bytes, not chars,
     so the builder's density chip has to count the same way. */
  function qrBytes(s) {
    s = String(s == null ? '' : s);
    var n = 0;
    for (var i = 0; i < s.length; i++) {
      var c = s.charCodeAt(i);
      if (c < 0x80) n += 1;
      else if (c < 0x800) n += 2;
      else if (c >= 0xd800 && c <= 0xdbff) { n += 4; i++; }
      else n += 3;
    }
    return n;
  }

  /* ── QR encoding ──────────────────────────────────────────────────────
     A private copy of the ss-render.js matrix path (ss-render.js:132-200), on
     purpose: this file is loaded alone in the press sidecar, and SS.drawQr is
     private there anyway.

     EC level M rather than the library's H default. H is there for a product
     whose codes are expected to be damaged in use; a contact label's is not,
     and M keeps a 35-byte mailto: at 33 modules instead of 41 — a third more
     pitch on foil, which is what decides whether a phone camera locks on.
     If the loaded encoder does not publish CorrectLevel we take its default. */
  function qrMatrix(text) {
    if (!window.SS_QRLIB || !text) return null;
    try {
      var opt = { text: String(text), useSVG: true };
      var CL = window.SS_QRLIB.CorrectLevel;
      if (CL && typeof CL.M === 'number') opt.correctLevel = CL.M;
      var holder = document.createElement('div');
      var qc = new window.SS_QRLIB(holder, opt);
      var model = qc._oQRCode;
      if (!model || typeof model.getModuleCount !== 'function') return null;
      var n = model.getModuleCount(), dark = [];
      for (var row = 0; row < n; row++) {
        for (var col = 0; col < n; col++) if (model.isDark(row, col)) dark.push([col, row]);
      }
      return dark.length ? { n: n, dark: dark } : null;
    } catch (_) { return null; }
  }

  /* Draws into rect[data-qr]. Returns true when every code in the tree is a
     REAL encoded matrix, false when any of them is still the placeholder.
     The placeholder is deliberately NOT QR-shaped noise: a seeded pattern that
     looks like a code and encodes nothing once reached the cart and a buyer
     scanned the thing they were buying (2026-08-05). This one reads as an
     empty frame, so nobody can mistake it for a finished label. */
  function drawQr(root) {
    var rects = root.querySelectorAll('rect[data-qr]');
    var allReal = true, any = false;
    for (var r = 0; r < rects.length; r++) {
      var rect = rects[r];
      var data = rect.getAttribute('data-qr') || '';
      if (!data) continue;
      any = true;
      var x = parseFloat(rect.getAttribute('x')), y = parseFloat(rect.getAttribute('y'));
      var w = parseFloat(rect.getAttribute('width'));
      if (!(w > 0)) continue;
      var prev = rect.parentNode.querySelector('g[data-lt-qr]');
      var real = qrMatrix(data);
      if (!real) {
        allReal = false;
        if (prev) continue;                      /* keep the frame we drew */
      }
      var N = real ? real.n : 25, m = w / (N + 2);   /* one-module quiet zone */
      var g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      g.setAttribute('data-lt-qr', '1');
      g.setAttribute('data-qr-real', real ? '1' : '0');
      var sq = function (cx, cy, size, fill, op) {
        var e = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        e.setAttribute('x', cx.toFixed(3)); e.setAttribute('y', cy.toFixed(3));
        e.setAttribute('width', size.toFixed(3)); e.setAttribute('height', size.toFixed(3));
        e.setAttribute('fill', fill);
        if (op) e.setAttribute('opacity', String(op));
        g.appendChild(e);
      };
      if (real) {
        for (var kk = 0; kk < real.dark.length; kk++) {
          sq(x + m + real.dark[kk][0] * m, y + m + real.dark[kk][1] * m, m * 1.04, '#111116');
        }
      } else {
        /* pending frame — three empty corner brackets at low opacity. Nobody
           can mistake it for a finished code. */
        var b = w * 0.22, t = Math.max(0.08, w * 0.035);
        var corners = [[x + m, y + m], [x + w - m - b, y + m], [x + m, y + w - m - b]];
        for (var ci = 0; ci < corners.length; ci++) {
          sq(corners[ci][0], corners[ci][1], b, '#111116', 0.3);
          sq(corners[ci][0] + t, corners[ci][1] + t, Math.max(0.01, b - 2 * t), '#ffffff', 0.95);
        }
      }
      if (prev && prev.parentNode) prev.parentNode.removeChild(prev);
      rect.parentNode.insertBefore(g, rect.nextSibling);
    }
    return any ? allReal : null;
  }

  /* The encoder is an ES module and lands AFTER first paint, so any label
     drawn with the pending frame is redrawn for real the moment it arrives.
     ss-render.js has a listener of its own but it only re-scans '.ss-seal svg'
     — a Lost & Found host is not one, so this file carries its own. */
  if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('ss-qr-ready', function () {
      try {
        var svgs = document.querySelectorAll('svg[data-lt="1"]');
        for (var i = 0; i < svgs.length; i++) {
          if (svgs[i].getAttribute('data-qr-real') === '0') {
            var ok = drawQr(svgs[i]);
            if (ok) svgs[i].setAttribute('data-qr-real', '1');
          }
        }
      } catch (_) {}
    });
  }

  /* ── shared furniture ─────────────────────────────────────────────────── */

  /* the reward strip: an INK BAND WITH FOIL KNOCKOUT WORDING. This is the one
     place on the label where knocking type out to bare stock is the best move
     rather than the lazy one — a short, tracked, all-caps line inside a bar
     small enough that the foil reads as a flash instead of a hole. On an
     already-inked ground the polarity flips (foil bar, ink wording) so the
     strip still separates from the plate. It never carries a printed amount.

     WHY THE BAND SITS ON THE PLATE'S FOOT on plain, plain_lg and callout_lg,
     and why the fix is not to nudge it up. The plate is already clamped to
     the 1.0 mm print-safe line, so it cannot grow downward to show foil
     under the band; the band would have to move up instead, and there is
     nowhere for it to go. Measured on artwork, in die millimetres, from the
     band's top to the bottom of the printed run above it:
       callout    0.86 mm of headroom — and 0.35 mm of foil already showing
       plain      0.77 mm
       plain_lg   0.46 mm — a 0.5 mm lift prints the band THROUGH the row
       callout_lg 0.10 mm — the detail stack is already against the band
     A 0.5 mm lift is free on exactly one of the three, and buying it on the
     other two costs a detail row its space. No ink crosses the plate as it
     stands — both band corners land on the straight part of the plate's
     foot, outside its 0.7 mm arcs — so this is composition and never a
     press hazard. */
  function rewardBar(o, c, k, x, y, w, h, size) {
    if (!c.reward) return;
    var barFill = k.dark ? FOIL : c.accent;
    var tFill = k.dark ? INK : FOIL;
    o.push('<path d="' + roundRect(x, y, w, h, Math.min(0.5, h * 0.22)) +
      '" fill="' + barFill + '"/>');
    o.push(txt(x + w / 2, y + h / 2 + size * 0.36, size, tFill, c.reward.toUpperCase(),
      { anchor: 'middle', ls: 0.12, fit: w - 1.2, hard: true, halo: false, role: 'reward' }));
  }

  /* ── THE FOIL PLATE ───────────────────────────────────────────────────
     What holds a contact label together over a full-bleed artwork, and the
     same move ss-seal.js makes over a Seal Studio background: the art bleeds
     to the die, then a panel of BARE FOIL goes down under the type and the
     type prints on it in ink (ss-seal.js:2181-2205, pillRect). Artwork that
     is beautiful behind a headline is noise behind a phone number, and a
     phone number a finder cannot read is the whole product failing.

     '#ffffff' here means what it means everywhere else in this file: NO INK
     LAID. The plate is not a white box printed over the picture — it is the
     picture stopping and the holographic stock showing through, which is why
     a preview repaints it with the iridescent sweep (foilify) and the press
     leaves it unprinted. That also makes the QR patch and the plate the same
     material, so a code sitting on a plate has no seam round it.

     GEOMETRY IS PER LAYOUT and each layout declares its own, because only the
     layout knows where its text column and its code column are. Two rules
     bind all of them:
       · the plate is CLAMPED into the 1.0 mm print-safe box, so it can never
         run off the cut, and every layout may therefore ask for more than it
         can have and let the clamp settle it;
       · it is sized off each column's FIT CAPS, not off the sample text. A
         name is capped at 28 characters and the plate has to be under all 28
         of them, not under the four a mock-up happened to use.
     A layout is free to ask for two panels; where its columns are closer than
     the plate's own corner radius it asks for one that spans both, because
     two panels 0.4 mm apart do not read as a gutter — they read as a scratch.

     Bounds default to the landscape die. drawStrip composes on a rotated
     portrait canvas and passes its own (bw, bh) so the clamp still means the
     print-safe box and not something rotated 90 degrees out of it. */
  /* ── THE SCREEN — the artwork ghosting THROUGH the plate ──────────────
     Owner, 2026-09-05: the picture has to be the star. A bare panel of foil
     under the type is what makes a phone number readable, but on the callout
     dies that panel is 82-85% of the label, so twenty-eight designed artworks
     were reading as a 1 mm rim. A blank panel is also not what the seal
     builder does — a seal shows its design across the whole card.

     So the plate is drawn exactly as before (bare foil, data-holo, the press
     leaves it unprinted) and then the SAME raster is laid over it a second
     time, clipped to the plate's own outline and screened back to ART_SCREEN.
     Full strength outside the panel, 30% inside it, one continuous picture.

     THE SECOND IMAGE IS THE FIRST ONE. Same href (one CDN fetch, one cache
     entry, one decode — the press sidecar preloads by URL and gets both for
     free), same x/y/width/height, same 'slice' cover-crop. It has to be the
     whole die and not the plate's own box: an image re-fit to the panel would
     be a DIFFERENT crop from the bleed behind it and the picture would jump at
     the plate edge, which is the one thing this is here to avoid.

     PRINT. A 30% raster on bare foil is lighter CMYK on unprinted stock. No
     white ink is assumed anywhere: the plate is still the material, the screen
     is ink on top of it, and the type is ink on top of that with its bare-foil
     halo knocking a gap around every glyph.

     PREVIEW. foilify() only ever repaints the literal '#ffffff' — so it swaps
     the plate for the iridescent sweep and leaves BOTH <image> elements
     exactly as written, which is what a customer has to see.

     `xf` counter-transforms a rotated composition. drawStrip authors its plate
     in a portrait sub-canvas under `translate(0 H) rotate(-90)`; the CLIP has
     to stay in that rotated space (it is the plate's own outline) while the
     image inside it has to land in die space, so the caller passes the exact
     inverse of its own group transform. */
  function screenArt(o, k, d, xf) {
    if (!k.art) return;
    var cid = k.id + 'k' + (++_plateN);
    o.push('<clipPath id="' + cid + '"><path d="' + d + '"/></clipPath>');
    o.push('<g clip-path="url(#' + cid + ')" opacity="' + ART_SCREEN + '">' +
      (xf ? '<g transform="' + xf + '">' : '') +
      '<image href="' + esc(k.art) + '" x="0" y="0" width="' + r2(k.W) +
      '" height="' + r2(k.H) + '" preserveAspectRatio="xMidYMid slice" ' +
      'data-lt-screen="1"/>' + (xf ? '</g>' : '') + '</g>');
  }

  function plateBox(o, k, x0, y0, x1, y1, bw, bh, xf) {
    if (!k.plate) return;
    var W = bw || k.W, H = bh || k.H;
    var ax = Math.max(SAFE, x0), ay = Math.max(SAFE, y0);
    var bx = Math.min(W - SAFE, x1), by = Math.min(H - SAFE, y1);
    if (!(bx - ax > 1.0 && by - ay > 1.0)) return;
    var d = roundRect(ax, ay, bx - ax, by - ay, PLATE_R);
    o.push('<path d="' + d + '" fill="' + FOIL + '" data-holo="1" data-lt-plate="1"/>');
    screenArt(o, k, d, xf);
  }

  /* A BARE-FOIL WINDOW — the move that stops an inked ground from swallowing
     the material. A rounded panel of unprinted stock punched through the ink
     with the type inside it set in INK, so even the black option shows the
     foil where the customer's own name is. On open stock there is no ink to
     punch through, so it draws nothing and the caller keeps the ground's own
     polarity. Returns the fill the type inside the window must use. */
  function foilWindow(o, k, x, y, w, h) {
    if (!k.grounded) return k.TXT;
    o.push('<path d="' + roundRect(x, y, w, h, Math.min(1.0, h * 0.16)) +
      '" fill="' + FOIL + '"/>');
    return INK;
  }

  /* the thin accent rule that makes open stock read as a composition instead
     of type floating on a blank sticker. It is always set in a TYPE GUTTER, so
     it can never strike through a glyph, and it is always a hairline: this
     product's furniture is drawn, not flooded. */
  function accentRule(o, c, k, x1, y, x2, op, w) {
    line(o, x1, y, x2, y, c.accent, op == null ? 0.9 : op, w || 0.18);
  }
  /* the vertical hairline between the text column and the code column */
  function seamRule(o, c, k, x, y1, y2) {
    line(o, x, y1, x, y2, k.TXT, 0.35, 0.15);
  }
  /* a knockout hairline INSIDE an ink band: bare stock flashing through the
     one heavy element a band layout owns. Nothing on an already-foil band. */
  function bandFlash(o, k, x1, y, x2) {
    if (k.dark) return;
    line(o, x1, y, x2, y, FOIL, 0.75, 0.14);
  }

  /* the QR patch: the one bare-foil rectangle on the label. White here means
     no ink laid, i.e. bright silver stock — the highest-contrast quiet zone
     this material can give a camera. data-holo marks it for the press path. */
  function qrPatch(o, k, x, y, w) {
    o.push('<rect x="' + r2(x) + '" y="' + r2(y) + '" width="' + r2(w) + '" height="' + r2(w) +
      '" rx="' + r2(Math.min(0.5, w * 0.045)) + '" fill="' + FOIL +
      '" data-holo="1" data-qr="' + esc(k.payload) + '"/>');
  }

  /* the hero line: the single thing a finder acts on. The phone if there is
     one, otherwise whatever route the customer did give us. Nothing on the
     label may out-size it. */
  function heroOf(c) {
    if (c.phone) return { key: 'phone', text: c.phone, big: true };
    if (c.email) return { key: 'email', text: c.email, big: false };
    if (c.social) return { key: 'social', text: '@' + c.social, big: false };
    if (c.e164) return { key: 'phone', text: c.e164, big: true };
    return { key: '', text: '', big: true };
  }

  /* the detail stack. Items are offered in priority order; the field backing
     the QR is promoted to the front so a code's destination always prints in
     type as well (copy spec §4d). Anything this layout has no row for is
     recorded in `dropped` — never silently lost. */
  function details(c, k, skipKey) {
    var made = [];
    function add(key, text) {
      if (!text || key === skipKey) return;
      if (k.carries.indexOf(key) < 0) { _dropped.push(key); return; }
      made.push({ key: key, text: text });
    }
    add('email', c.email);
    add('social', c.social ? '@' + c.social : '');
    add('addr1', c.addr1);
    add('addr2', c.addr2);
    add('note', c.note);
    var promote = { email: 'email', instagram: 'social', web: '' }[c.qa];
    if (promote) {
      for (var i = 0; i < made.length; i++) {
        if (made[i].key === promote) { made.unshift(made.splice(i, 1)[0]); break; }
      }
    }
    return made;
  }
  /* print as many detail rows as this layout has slots for; report the rest */
  function stack(o, k, items, rows) {
    var i = 0;
    for (; i < items.length && i < rows.length; i++) {
      var it = items[i], rw = rows[i];
      o.push(txt(rw.x, rw.y, rw.size, k.TXT, it.text,
        { w: false, anchor: rw.anchor, fit: rw.fit, hard: true, op: 0.95, role: it.key }));
    }
    for (; i < items.length; i++) _dropped.push(items[i].key);
  }
  /* The printed fallback under a code. 'TEXT +44 …' and 'WHATSAPP +44 …' are
     the strings the copy spec prints, but they do not fit a patch-width slot
     on one line without shrinking the NUMBER — the one thing on that line a
     finder has to be able to read. So the prefix becomes its own small label
     line above the value, and the value keeps its size. */
  function fallbackLines(k) {
    if (!k.fallback) return [];
    var m = /^(TEXT|WHATSAPP)\s+(.+)$/.exec(k.fallback);
    return m ? [m[1], m[2]] : [k.fallback];
  }
  function drawFallback(o, k, cx, y, size, fitw) {
    var ln = fallbackLines(k);
    if (!ln.length) return;
    if (ln.length === 1) {
      o.push(txt(cx, y, size, k.TXT, ln[0],
        { anchor: 'middle', fit: fitw, hard: true, w: false, role: 'qr-fallback' }));
      return;
    }
    o.push(txt(cx, y, Math.max(MIN_TEXT, size * 0.86), k.TXT, ln[0],
      { anchor: 'middle', ls: 0.16, op: 0.7, fit: fitw, hard: true, role: 'qr-fallback-label' }));
    o.push(txt(cx, y + size * 1.38, size, k.TXT, ln[1],
      { anchor: 'middle', fit: fitw, hard: true, w: false, role: 'qr-fallback' }));
  }

  /* two-line header split at the space nearest the middle */
  function split2(s, maxLen) {
    s = String(s || '');
    if (s.length <= maxLen || s.indexOf(' ') < 0) return [s];
    var mid = Math.floor(s.length / 2), best = -1;
    for (var i = 0; i < s.length; i++) {
      if (s.charAt(i) === ' ' && (best < 0 || Math.abs(i - mid) < Math.abs(best - mid))) best = i;
    }
    if (best < 0) return [s];
    return [s.slice(0, best).trim(), s.slice(best + 1).trim()];
  }

  /* ═══ STANDARD DIE — 44.958 x 19.05 mm, M 1.2 ════════════════════════ */

  /* ── THE ARTWORK COLUMN ───────────────────────────────────────────────
     A layout with a code splits into a text column and a code column. Without
     one it REFLOWS: the code column's width goes back to the text and the
     detail rows open up. That reflow is right on every other ground and wrong
     on an artwork one — a full-width text column takes a full-width plate, the
     artwork is left a 1 mm frame nobody can identify a design by, and the
     twenty-eight grounds all print as the same label.

     So on an ART GROUND the column stays where it is whether there is a code
     or not, and the space the reflow would have taken becomes an ARTWORK
     COLUMN: the code sits in it when there is one, and it is open artwork when
     there is not. A design with no code gives the ground 44% of the label
     instead of 15% and costs nothing but text width the fit pass absorbs.

     wideCol() is that decision, in one place. It says NO on an artwork ground.
     The two `plain` layouts have no column to keep — they are centred, and
     "Full Contact" is the option that spends the whole label on type — so they
     take the full plate and say so. */
  function wideCol(k) { return !k.hasQr && !k.plate; }

  /* ── L0 · PHOTO — "BIG PICTURE" ───────────────────────────────────────
     The style the artwork catalogue is actually for. Every other layout on
     this die is a contact card that happens to have a picture behind it; this
     one is a picture that happens to carry a phone number.

     WHAT IT DROPS, AND WHY. The header, the email, the social handle and the
     address are all gone — not squeezed, gone. Four rows of small type are
     what turn a 45 mm sticker into a form, and a form needs a panel under all
     of it. Cutting them is what buys the picture back: with a code this
     layout's panels are 44% of the label against the callout's 82-85%, and
     without one 36%. What survives is the two things a finder acts on — a NAME
     and a NUMBER, the number set as the hero — plus the reward, which is one
     thin band on the panel's own bottom edge and costs no height of its own.
     Anything the customer typed that this cannot carry is reported in
     `dropped`, so the builder names it and offers the styles that keep it.

     THE CODE gets a small panel of its own in the right-hand corner, with its
     caption over it and its printed fallback under it. qrActions is
     call/text/whatsapp only: those are the three whose fallback IS the phone
     number, so nothing is promised here that the label does not also print in
     type — and the number is already the hero.

     NO LOGO SLOT. A logo needs a quiet backing to read on, logoBox only draws
     one on a ground that carries ink, and an artwork ground carries none — so
     a mark would land raw on the picture. Reported, never silently swallowed.

     The hero: the number as the customer typed it, else the E.164 the builder
     composed. Only if there is no number at all does it fall back to whatever
     route there is, rather than print a label a finder cannot act on. */
  function photoHero(c) {
    if (c.phone) return { key: 'phone', text: c.phone, big: true };
    if (c.e164) return { key: 'phone', text: c.e164, big: true };
    return heroOf(c);
  }

  /* L0 · PHOTO — standard die. Panels: 21.9 x 8.35 for the type and
     12.46 x 15.65 for the code = 377.9 mm² of 856.45, i.e. 44.1%. Without a
     code the type panel opens to 32.4 x 9.55 and the total is 36.1%. */
  function drawPhoto(o, c, k) {
    if (c.logo) _dropped.push('logo');
    var q = k.hasQr;
    var px1 = q ? 22.9 : 33.4;                    /* the type panel's right edge */
    plateBox(o, k, 1.0, q ? 9.7 : 8.5, px1, 18.05);
    if (q) plateBox(o, k, 31.5, 2.4, 43.96, 18.05);
    var x = q ? 2.0 : 2.1, fitw = q ? 20.0 : 30.3;
    var hero = photoHero(c);
    var nSize = q ? (c.reward ? 2.45 : 2.55) : (c.reward ? 3.10 : 3.30);
    var ny = q ? (c.reward ? 12.35 : 12.5) : (c.reward ? 11.9 : 12.2);
    var hy = q ? (c.reward ? 15.1 : 16.4) : (c.reward ? 15.3 : 16.5);
    var hSize = q ? (c.reward ? 2.70 : 3.10) : (c.reward ? 3.50 : 3.90);
    o.push(txt(x, ny, nSize, k.TXT, c.name.toUpperCase(),
      { ls: 0.05, fit: fitw, hard: true, role: 'name' }));
    o.push(txt(x, hy, hero.big ? hSize : hSize * 0.74, k.TXT, hero.text,
      { fit: fitw, hard: false, role: hero.key || 'hero' }));
    /* the fields this style has no row for are REPORTED, not lost — details()
       records every one the layout does not carry */
    details(c, k, hero.key);
    /* the reward band sits ON the panel's bottom edge: ink with the wording
       knocked out to bare foil, the same strip every other layout uses */
    if (q) rewardBar(o, c, k, 1.8, 15.75, 20.3, 1.85, 1.10);
    else rewardBar(o, c, k, 1.8, 15.85, 30.8, 1.90, 1.15);
    if (q) {
      /* the code column. The CAPTION sets its width — "SCAN FOR WHATSAPP" is
         10.59 mm at the 0.95 mm floor with its letter-spacing already spent —
         and 12.46 mm of panel carries that with 0.68 mm of padding. The patch
         takes 10.4 of it, leaving 1.03 mm of panel round the code on top of
         the one-module quiet zone drawQr draws inside it. The panel's height
         is set by the caption over it and the printed fallback under it, and
         it had 1.5 mm spare: that went into the code rather than into empty
         foil. This layout's widest payload is a 32-byte sms (a version 3,
         29-module code), so the pitch is 10.4/31 = 0.34 mm — well above the
         0.264 mm floor measured on this stock. */
      var qcx = 37.73, qw = 10.4;
      if (k.caption) micro(o, k, qcx, 3.9, k.caption, k.TXT,
        { anchor: 'middle', fit: 11.1, op: 0.7, role: 'qr-caption' });
      qrPatch(o, k, qcx - qw / 2, 4.35, qw);
      drawFallback(o, k, qcx, 15.9, 1.05, 11.1);
    }
  }

  /* L1 · CALLOUT — the default, and the first thing a customer sees: bare
     foil, ink type, one accent hairline in the header gutter and a hairline
     seam holding the code column. Reflows to a full-width text panel when
     there is no code — unless the ground is artwork, which keeps the column
     open for the picture. */
  function drawCallout(o, c, k) {
    var wide = wideCol(k);
    /* PLATE. With a code it is ONE panel, and on this die that is forced
       rather than chosen. It is the CAPTION and not the patch that sets the
       width of a code column: "SCAN FOR WHATSAPP" measures 10.59 mm at the
       0.95 mm readability floor with its letter-spacing already spent, so the
       column needs 11.6 mm of plate to carry it with padding, its right edge
       is pinned at 43.9, and the text column's own plate cannot end before
       31.3. That leaves a 1.0 mm gutter — narrower than the plate's 1.4 mm
       corner diameter, so two panels there would read as a scratch rather than
       as a gutter, and the label would have paid for it in QR pitch. Shrinking
       the patch alone buys nothing here: the width it releases is caption, not
       artwork. The two large-die callouts and both banners DO have the room and
       take it (drawCalloutLg, drawBannerLg, drawBanner).
       Without a code on an artwork ground the plate stops at the column and the
       right 13 mm is picture. The QR caption sets the top when there is a code
       (its cap lands at 1.4) and the logo sets it otherwise; with neither, the
       header's cap at 2.85 leaves 2.05 mm of artwork open across the head. */
    plateBox(o, k, 1.1, (c.logo || k.hasQr) ? 0.9 : 2.05,
             k.hasQr ? 43.9 : (wide ? 43.6 : 31.3), 18.6);
    var x = 2.0, right = wide ? 42.7 : 30.6;
    var hx = x, hfit = right - x;
    if (c.logo) { o.push(logoBox(c, k, x, 1.6, 3.3, 3.3)); hx = x + 4.1; hfit = right - hx; }
    if (c.hdr) o.push(txt(hx, 3.7, 1.15, k.MUT, c.hdr.toUpperCase(),
      { ls: 0.16, op: 0.9, fit: hfit, hard: true, role: 'header' }));
    accentRule(o, c, k, hx, 4.4, right);
    if (k.hasQr) seamRule(o, c, k, 31.15, 2.1, 17.0);
    var hero = heroOf(c);
    o.push(txt(x, 7.6, 2.90, k.TXT, c.name.toUpperCase(),
      { ls: 0.05, fit: right - x, hard: true, role: 'name' }));
    o.push(txt(x, 11.6, hero.big ? 3.30 : 2.40, k.TXT, hero.text,
      { fit: right - x, hard: false, role: hero.key || 'hero' }));
    var rows = c.reward
      ? [{ x: x, y: 14.2, size: 1.50, fit: right - x }]
      : [{ x: x, y: 14.2, size: 1.50, fit: right - x },
         { x: x, y: 16.1, size: 1.35, fit: right - x },
         { x: x, y: 17.6, size: 1.20, fit: right - x }];
    stack(o, k, details(c, k, hero.key), rows);
    rewardBar(o, c, k, 1.8, 15.4, (wide ? 41.1 : 29.0), 2.3, 1.25);
    if (k.hasQr) {
      var qx = 31.9, qw = 11.0, qcx = qx + qw / 2;
      if (k.caption) micro(o, k, qcx, 2.15, k.caption, k.TXT,
        { anchor: 'middle', fit: 11.8, op: 0.7, role: 'qr-caption' });
      qrPatch(o, k, qx, 2.6, qw);
      drawFallback(o, k, qcx, 15.4, 1.05, 11.6);
    }
  }

  /* L2 · PLAIN — no code at all, maximum type. The layout that proves the
     product works for someone who never scans anything. */
  function drawPlain(o, c, k) {
    /* PLATE, and the one that DOES take the whole label. Everything here is
       centred on caps of 41.5-41.9 mm out of 44.958, so a panel that carries
       all of it is a panel the width of the print-safe box: on an artwork
       ground this layout shows the picture as a border and nothing more.
       That is the deal, not an oversight. "Full Contact" is the option that
       spends the entire label on type — it is the one a customer picks when
       the address and the note matter more than the design — and the way to
       buy the artwork its column back is to pick any of the other six
       layouts. Narrowing the caps here to open a margin would shrink the very
       thing this layout exists to print. */
    plateBox(o, k, 0.9, c.logo ? 1.2 : 1.6, 44.1, 18.7);
    var cx = k.W / 2, fitw = 41.5;
    if (c.logo) { o.push(logoBox(c, k, 1.9, 1.9, 3.4, 3.4)); fitw = 32.0; }
    if (c.hdr) o.push(txt(cx, 3.4, 1.30, k.MUT, c.hdr.toUpperCase(),
      { ls: 0.18, op: 0.9, anchor: 'middle', fit: fitw, hard: true, role: 'header' }));
    /* a double rule under the header — the only furniture this layout owns,
       and the reason a plate of open stock reads as a composed label */
    accentRule(o, c, k, 6.0, 4.2, 38.9);
    accentRule(o, c, k, 14.0, 4.78, 30.9, 0.45, 0.13);
    var hero = heroOf(c);
    o.push(txt(cx, 8.0, 3.40, k.TXT, c.name.toUpperCase(),
      { anchor: 'middle', ls: 0.05, fit: 41.5, hard: true, role: 'name' }));
    o.push(txt(cx, 12.6, hero.big ? 4.20 : 2.90, k.TXT, hero.text,
      { anchor: 'middle', fit: 41.9, hard: false, role: hero.key || 'hero' }));
    var rows = c.reward
      ? [{ x: cx, y: 15.0, size: 1.70, fit: 41.5, anchor: 'middle' }]
      : [{ x: cx, y: 15.0, size: 1.70, fit: 41.5, anchor: 'middle' },
         { x: cx, y: 16.55, size: 1.30, fit: 41.5, anchor: 'middle' },
         { x: cx, y: 17.72, size: 1.15, fit: 41.5, anchor: 'middle' }];
    stack(o, k, details(c, k, hero.key), rows);
    rewardBar(o, c, k, 2.4, 16.15, k.W - 4.8, 1.9, 1.25);
  }

  /* L3 · BANNER — an ink band across the top carrying the header in knockout.
     No room under the patch for a caption, so this layout only accepts codes
     whose destination already prints in the text column (LAYOUTS.qrActions). */
  function drawBanner(o, c, k) {
    /* the band is the only heavy element here. On open stock it is ink with
       the header knocked out to bare foil — the letters ARE the material — and
       a knockout hairline under them flashes the stock a second time. */
    var wide = wideCol(k), right = wide ? 42.7 : 30.4;
    /* PLATE. The band is opaque ink and carries its own knockout header, so
       the plate starts UNDER it — 0.4 mm of artwork survives between the two.
       On an artwork ground the BAND stops at the column too: a full-width band
       over a picture takes the top of the label as well as the middle, and the
       point of the column is that it runs the whole height.
       This layout prints no caption and no printed fallback either, so its code
       column is the patch and nothing else. On an artwork ground that is 9.3 mm
       (9.3/31 = 0.30 mm of pitch at the worst-case 32-byte version 3 code,
       above the 0.28 mm design pitch) on a panel of its own, leaving 2.2 mm of
       picture down the full height between the two. */
    var bandR = k.plate ? (right + 0.6) : (k.W - 1.0);
    plateBox(o, k, 1.6, 6.0, wide ? 43.4 : 31.0, 18.6);
    if (k.hasQr) plateBox(o, k, 33.2, 6.0, 43.7, 18.6);
    var bandFill = k.dark ? FOIL : INK, bandTxt = k.dark ? INK : FOIL;
    o.push('<path d="' + roundRect(1.0, 1.0, bandR - 1.0, 4.6, 0.7) + '" fill="' + bandFill + '"/>');
    /* the header centres on the BAND, whatever the band's width now is; the
       caps stay exactly what they were when the band is full width, so no
       other ground's svg moves by a hundredth of a millimetre */
    var hcx = (1.0 + bandR) / 2, hfit = k.plate ? bandR - 4.0 : 40.0;
    if (c.logo) {
      o.push(logoBox(c, k, 1.9, 1.55, 3.5, 3.5));
      hcx = (5.9 + bandR - 0.6) / 2; hfit = k.plate ? bandR - 9.9 : 34.0;
    }
    if (c.hdr) o.push(txt(hcx, 4.3, 1.50, bandTxt, c.hdr.toUpperCase(),
      { ls: 0.2, anchor: 'middle', fit: hfit, hard: true, halo: false, role: 'header' }));
    bandFlash(o, k, 2.4, 5.05, bandR - 1.4);
    var x = 2.2;
    var hero = heroOf(c);
    /* on any ground that carries ink the name and the number sit in a window
       of bare stock, so the customer's own line is printed ON the foil */
    var win = foilWindow(o, k, 1.7, 6.35, (right + 0.5) - 1.7, 7.7);
    o.push(txt(x, 9.2, 2.70, win, c.name.toUpperCase(),
      { ls: 0.05, fit: right - x, hard: true, role: 'name' }));
    o.push(txt(x, 13.0, hero.big ? 3.10 : 2.30, win, hero.text,
      { fit: right - x, hard: false, role: hero.key || 'hero' }));
    var rows = c.reward
      ? [{ x: x, y: 15.6, size: 1.45, fit: right - x }]
      : [{ x: x, y: 15.6, size: 1.45, fit: right - x },
         { x: x, y: 17.5, size: 1.25, fit: right - x }];
    stack(o, k, details(c, k, hero.key), rows);
    if (c.reward) o.push(txt(x, 17.6, 1.10, k.TXT, c.reward.toUpperCase(),
      { ls: 0.12, fit: right - x, hard: true, op: 0.95, role: 'reward' }));
    if (k.hasQr) qrPatch(o, k, k.plate ? 33.7 : 31.6, 6.0, k.plate ? 9.3 : 11.4);
  }

  /* L4 · STRIP — the same die read the other way round, for a bottle, a
     laptop edge, a tripod leg, a cable wrap. The cell stays landscape (that is
     what the press cuts); the composition is authored portrait and rotated
     into it, so the label reads correctly when it is applied vertically. */
  function drawStrip(o, c, k) {
    /* the portrait sub-canvas: 19.05 wide x 44.958 tall, rotated into the
       landscape cell the press actually cuts */
    var PW = k.H;
    var g = ['<g transform="translate(0 ' + r2(k.H) + ') rotate(-90)">'];
    /* PLATE, authored in the PORTRAIT canvas like everything else in `g` — it
       rides the rotation and lands square on the die. The clamp is handed the
       portrait bounds (19.05 wide x 44.958 tall) so "1.0 mm print-safe" still
       means the same millimetre after the rotation. Portrait y is landscape x,
       so the reward bar's foot is what leaves the artwork its band: 1.36 mm at
       the label's right edge with a code, 3.86 mm without one.
       The screen inside it has to land in DIE space, not in this rotated one,
       or the artwork would ghost through sideways — so the plate is handed the
       exact inverse of the group transform above (screenArt's `xf`). */
    plateBox(g, k, 0.5, 1.5, 18.5, k.hasQr ? 43.6 : 41.1, PW, k.W,
             'rotate(90) translate(0 ' + r2(-k.H) + ')');
    /* 19.05 mm is the narrowest run this shop prints and the stack fills it,
       so there is no artwork column to keep here either — what the ground gets
       is the band past the reward bar's foot and the border. */
    var cx = PW / 2;
    /* 19.05 mm of width is the narrowest run this shop prints. A logo would
       cost the header or the fallback its line, so this one layout has no logo
       slot — it is reported, never silently swallowed, and the builder greys
       the uploader out for it. */
    if (c.logo) _dropped.push('logo');
    if (c.hdr) {
      var lines = split2(c.hdr, 15);
      for (var i = 0; i < lines.length; i++) {
        g.push(txt(cx, 3.2 + i * 1.8, 1.20, k.MUT, lines[i].toUpperCase(),
          { ls: 0.14, op: 0.9, anchor: 'middle', fit: 16.0, hard: true, role: 'header' }));
      }
    }
    /* the rule lands in a gutter of the PORTRAIT canvas, so it rides the
       rotation with everything else in `g`. 19.05 mm of width is this shop's
       narrowest run and the gutters here are thin: a two-line header sitting
       above a QR caption leaves no room at all, and a hairline drawn through a
       caption is worse than no hairline (the overlap audit only sees text, so
       nothing downstream would catch it). */
    var hLines = c.hdr ? split2(c.hdr, 15).length : 1;
    var ruleY = hLines >= 2 ? 6.3 : 4.25;
    var capTop = (k.hasQr && k.caption) ? 5.75 : 99;
    if (ruleY + 0.2 < capTop) accentRule(g, c, k, 1.9, ruleY, 17.15);
    var patchY = 7.2;
    var hero = heroOf(c);
    var qw = 14.6;
    if (k.hasQr) {
      /* the patch is authored in the portrait sub-canvas and rides the group's
         rotation with everything else — drawQr reads the rect's own x/y/width
         and inserts the modules as its sibling, so they inherit the transform */
      g.push('<rect x="' + r2((PW - qw) / 2) + '" y="' + r2(patchY) + '" width="' + r2(qw) +
        '" height="' + r2(qw) + '" rx="0.5" fill="' + FOIL + '" data-holo="1" data-qr="' +
        esc(k.payload) + '"/>');
      if (k.caption) g.push(txt(cx, patchY - 0.7, Math.max(MIN_TEXT, k.SZ.micro), k.TXT,
        k.caption.toUpperCase(), { ls: 0.2, op: 0.7, anchor: 'middle', fit: 15.6,
          hard: true, role: 'qr-caption' }));
      drawFallback(g, k, cx, patchY + qw + 1.6, 1.10, 15.6);
    }
    /* a full name does not fit 16.4 mm on one line at a readable size, so this
       layout gives it two — shrinking it to the floor and dropping characters
       would print a name that is not the customer's name */
    var nl = split2(c.name, 13), two = nl.length > 1;
    var nSize = k.hasQr ? 2.40 : 3.20;
    var ny = k.hasQr ? (two ? 27.4 : 27.8) : (two ? 12.4 : 13.5);
    for (var ni = 0; ni < nl.length; ni++) {
      g.push(txt(cx, ny + ni * nSize * 1.15, nSize, k.TXT, nl[ni].toUpperCase(),
        { anchor: 'middle', ls: 0.05, fit: 16.4, hard: true, role: 'name' }));
    }
    var py = k.hasQr ? (two ? 33.2 : 32.2) : (two ? 20.6 : 19.5);
    g.push(txt(cx, py, (k.hasQr ? 2.90 : 3.80) * (hero.big ? 1 : 0.78), k.TXT, hero.text,
      { anchor: 'middle', fit: 16.8, hard: false, role: hero.key || 'hero' }));
    var rows = k.hasQr
      ? [{ x: cx, y: 35.0, size: 1.35, fit: 16.4, anchor: 'middle' },
         { x: cx, y: 37.0, size: 1.25, fit: 16.4, anchor: 'middle' },
         { x: cx, y: 39.0, size: 1.25, fit: 16.4, anchor: 'middle' }]
      : [{ x: cx, y: 23.8, size: 1.60, fit: 16.4, anchor: 'middle' },
         { x: cx, y: 26.4, size: 1.50, fit: 16.4, anchor: 'middle' },
         { x: cx, y: 28.9, size: 1.50, fit: 16.4, anchor: 'middle' },
         { x: cx, y: 31.4, size: 1.50, fit: 16.4, anchor: 'middle' },
         { x: cx, y: 33.9, size: 1.50, fit: 16.4, anchor: 'middle' }];
    if (!c.reward && k.hasQr) rows.push({ x: cx, y: 41.0, size: 1.25, fit: 16.4, anchor: 'middle' });
    /* NOTE: everything this layout draws goes into `g`, never into `o` — `o` is
       the landscape die and `g` is the rotated portrait canvas. A run pushed to
       the wrong one lands sideways, off the label. */
    stack(g, k, details(c, k, hero.key), rows);
    rewardBar(g, c, k, (PW - 15.6) / 2, k.hasQr ? 40.3 : 37.4, 15.6,
      k.hasQr ? 2.5 : 3.0, k.hasQr ? 1.15 : 1.45);
    g.push('</g>');
    o.push(g.join(''));
  }

  /* ═══ LARGE DIE — 63.5 x 25.4 mm, M 1.4 ══════════════════════════════ */

  /* L0L · PHOTO_LG — "BIG PICTURE" on the large die. Same bargain as drawPhoto
     (see it for why the header and the detail rows are gone), rebuilt by hand
     for 63.5 x 25.4 rather than scaled. Panels: 28.6 x 12.4 for the type and
     13.5 x 19.9 for the code = 623.2 mm² of 1612.9, i.e. 38.6%. Without a code
     the type panel opens to 42.6 x 13.4 and the total is 35.4%. */
  function drawPhotoLg(o, c, k) {
    if (c.logo) _dropped.push('logo');
    var q = k.hasQr;
    var px1 = q ? 29.6 : 43.6;
    plateBox(o, k, 1.0, q ? 12.0 : 11.0, px1, 24.4);
    if (q) plateBox(o, k, 49.0, 4.5, 62.5, 24.4);
    var x = 2.4, fitw = q ? 26.2 : 40.2;
    var hero = photoHero(c);
    var nSize = q ? (c.reward ? 3.70 : 3.90) : (c.reward ? 4.50 : 4.70);
    var ny = q ? (c.reward ? 15.9 : 16.1) : (c.reward ? 15.6 : 16.0);
    var hy = q ? (c.reward ? 20.1 : 21.3) : (c.reward ? 20.4 : 21.6);
    var hSize = q ? (c.reward ? 4.10 : 4.60) : (c.reward ? 5.10 : 5.60);
    o.push(txt(x, ny, nSize, k.TXT, c.name.toUpperCase(),
      { ls: 0.05, fit: fitw, hard: true, role: 'name' }));
    o.push(txt(x, hy, hero.big ? hSize : hSize * 0.74, k.TXT, hero.text,
      { fit: fitw, hard: false, role: hero.key || 'hero' }));
    details(c, k, hero.key);
    if (q) rewardBar(o, c, k, 2.0, 20.9, 26.6, 2.3, 1.35);
    else rewardBar(o, c, k, 2.0, 21.2, 40.6, 2.4, 1.40);
    if (q) {
      /* 13.5 mm of panel: "SCAN FOR WHATSAPP" is 11.71 mm at this die's
         1.05 mm micro size, so 12.1 mm of caption fit clears it with 0.7 mm of
         padding each side. The patch takes 12.0 and this die's 64-byte budget
         is a version 5, 37-module code — 12.0/39 = 0.31 mm of pitch, the same
         as the flagship callout's. */
      var qcx = 55.75, qw = 12.0;
      if (k.caption) micro(o, k, qcx, 6.3, k.caption, k.TXT,
        { anchor: 'middle', fit: 12.1, op: 0.7, role: 'qr-caption' });
      qrPatch(o, k, qcx - qw / 2, 6.9, qw);
      drawFallback(o, k, qcx, 21.0, 1.35, 12.1);
    }
  }

  /* L1L · CALLOUT_LG — the flagship: every field, the code, the caption and
     the printed fallback all on one label. */
  function drawCalloutLg(o, c, k) {
    var wide = wideCol(k);
    /* THE CODE COLUMN, and how much of it the artwork gets back.
       16.0 mm of patch on every other ground, because there is nothing else on
       the label to spend that width on and a bigger code is a code a phone
       locks on to sooner. On an ARTWORK ground there IS something else to
       spend it on, so the column narrows to what its own type actually needs.
       Measured, not guessed: the widest caption this product prints ("SCAN FOR
       WHATSAPP") is 11.71 mm at the 1.05 mm micro size once fit() has spent
       its letter-spacing, and the printed fallback under it is narrower again.
       13.0 mm of fit carries both with room, 14.2 mm of plate carries that
       with padding, and the patch takes 12.2 of it — at EC-M a 64-byte payload
       is a version 5 code (37 modules) drawn with a one-module quiet zone each
       side, so 12.2/39 = 0.31 mm of pitch, above this family's 0.28 mm design
       pitch and clear of the 0.264 mm floor this shop has measured on
       holographic stock. */
    var qw = k.plate ? 12.2 : 16.0;
    var qcx = k.plate ? 55.4 : 53.6, qfit = k.plate ? 13.0 : 16.6;
    /* PLATE. On every other ground plateBox draws nothing and both calls are
       free. On an ARTWORK ground with a code it is TWO panels, the way
       card_lg's already are: the text column ends at the reward bar's 44.2 and
       the code column's panel opens at 48.3, so 3.4 mm of picture runs the
       FULL HEIGHT of the label between them — two and a half times the plate's
       own 1.4 mm corner diameter, which is what decides whether a gutter reads
       as a gutter or as a scratch. Without a logo the head of the label keeps
       2.4-3.1 mm of open artwork, and with no code at all the right 18 mm is
       picture. */
    plateBox(o, k, 1.7, c.logo ? 1.2 : (k.hasQr ? 2.4 : 3.1),
             wide ? 61.8 : 44.9, 25.0);
    if (k.hasQr) plateBox(o, k, 48.3, 2.4, 62.5, 25.0);
    var x = 2.6, right = wide ? 61.0 : 44.2;
    var hx = x, hfit = right - x;
    if (c.logo) { o.push(logoBox(c, k, x, 1.9, 4.0, 4.0)); hx = x + 4.9; hfit = right - hx; }
    if (c.hdr) o.push(txt(hx, 5.0, 1.50, k.MUT, c.hdr.toUpperCase(),
      { ls: 0.18, op: 0.9, fit: hfit, hard: true, role: 'header' }));
    accentRule(o, c, k, hx, 6.2, right);
    /* the seam is what holds a code column that has no gutter to hold it. An
       artwork ground HAS one, and a hairline drawn down the middle of it would
       be ink laid straight on the picture with no plate under it. */
    if (k.hasQr && !k.plate) seamRule(o, c, k, 44.9, 2.6, 23.2);
    var hero = heroOf(c);
    o.push(txt(x, 10.4, 3.80, k.TXT, c.name.toUpperCase(),
      { ls: 0.05, fit: right - x, hard: true, role: 'name' }));
    o.push(txt(x, 16.0, hero.big ? 4.60 : 3.20, k.TXT, hero.text,
      { fit: right - x, hard: false, role: hero.key || 'hero' }));
    var rows = c.reward
      ? [{ x: x, y: 18.9, size: 1.75, fit: right - x },
         { x: x, y: 20.6, size: 1.45, fit: right - x },
         { x: x, y: 22.1, size: 1.30, fit: right - x }]
      : [{ x: x, y: 18.9, size: 1.75, fit: right - x },
         { x: x, y: 20.6, size: 1.45, fit: right - x },
         { x: x, y: 22.2, size: 1.40, fit: right - x },
         { x: x, y: 23.7, size: 1.30, fit: right - x }];
    stack(o, k, details(c, k, hero.key), rows);
    rewardBar(o, c, k, 2.4, 22.5, (wide ? 58.7 : 41.8), 1.9, 1.15);
    if (k.hasQr) {
      if (k.caption) micro(o, k, qcx, 4.0, k.caption, k.TXT,
        { anchor: 'middle', fit: qfit, op: 0.7, role: 'qr-caption' });
      /* the narrower patch is re-centred between the caption and the printed
         fallback rather than left hanging under the caption */
      qrPatch(o, k, qcx - qw / 2, k.plate ? 6.9 : 4.7, qw);
      drawFallback(o, k, qcx, 22.0, 1.35, qfit);
    }
  }

  /* L2L · PLAIN_LG — centred, no code, and the biggest number this product
     prints: 6.4 mm reads across a baggage carousel. */
  function drawPlainLg(o, c, k) {
    /* PLATE. Full width for the same reason drawPlain's is, and with the same
       deal behind it: centred caps of 58.5-59.0 mm on a 63.5 mm die. The 6.4 mm
       hero is the biggest number this product prints and pulling the caps in to
       open an artwork margin would take millimetres off it. 1.55 mm down each
       side and 1.95 over the header is what is left. */
    plateBox(o, k, 1.55, c.logo ? 1.3 : 1.95, 61.95, 25.0);
    var cx = k.W / 2, hfit = 58.5;
    if (c.logo) { o.push(logoBox(c, k, 2.4, 2.0, 4.4, 4.4)); hfit = 46.0; }
    if (c.hdr) o.push(txt(cx, 4.6, 2.20, k.MUT, c.hdr.toUpperCase(),
      { ls: 0.2, op: 0.9, anchor: 'middle', fit: hfit, hard: true, role: 'header' }));
    accentRule(o, c, k, 8.0, 6.15, 55.5);
    accentRule(o, c, k, 20.0, 6.95, 43.5, 0.45, 0.14);
    var hero = heroOf(c);
    o.push(txt(cx, 11.6, 4.40, k.TXT, c.name.toUpperCase(),
      { anchor: 'middle', ls: 0.05, fit: 58.5, hard: true, role: 'name' }));
    o.push(txt(cx, 18.4, hero.big ? 6.40 : 4.20, k.TXT, hero.text,
      { anchor: 'middle', fit: 59.0, hard: false, role: hero.key || 'hero' }));
    /* the address prints as one line here — two short lines side by side read
       worse than one at this width */
    var items = details(c, k, hero.key);
    for (var i = 0; i < items.length - 1; i++) {
      if (items[i].key === 'addr1' && items[i + 1].key === 'addr2') {
        items[i] = { key: 'addr1', text: items[i].text + ' · ' + items[i + 1].text };
        items.splice(i + 1, 1);
        break;
      }
    }
    var rows = c.reward
      ? [{ x: cx, y: 21.6, size: 1.60, fit: 58.5, anchor: 'middle' }]
      : [{ x: cx, y: 21.4, size: 2.10, fit: 58.5, anchor: 'middle' },
         { x: cx, y: 23.7, size: 1.60, fit: 58.5, anchor: 'middle' }];
    stack(o, k, items, rows);
    rewardBar(o, c, k, 2.4, 22.4, k.W - 4.8, 2.0, 1.30);
  }

  /* L3L · BANNER_LG — the band, the reward at its right end, the code right.
     Like the standard banner it has no caption slot, so the text column
     discharges the printed-fallback duty (LAYOUTS.qrActions). */
  function drawBannerLg(o, c, k) {
    var wide = wideCol(k), right = wide ? 61.2 : 43.0;
    /* PLATE. Under the band, as on the standard banner; the reward rides the
       band's right end in knockout and needs no plate of its own. On an
       artwork ground the band stops at the column so the picture runs the full
       height of the label rather than starting a third of the way down.
       This layout prints NO caption and NO printed fallback (qrCaption false —
       the text column discharges that duty), so on an artwork ground the code
       column is only as wide as the patch itself: 12.6 mm, the same as
       card_lg's, 12.6/39 = 0.32 mm of pitch at the worst-case 64-byte version
       5 code. Its own panel opens at 46.9 and 3.3 mm of picture runs the full
       height between the two. */
    var bandR = k.plate ? (right + 0.6) : (k.W - 1.0);
    plateBox(o, k, 2.1, 7.85, wide ? 61.9 : 43.6, 25.0);
    if (k.hasQr) plateBox(o, k, 46.9, 7.85, 60.7, 25.0);
    var bandFill = k.dark ? FOIL : INK, bandTxt = k.dark ? INK : FOIL;
    o.push('<path d="' + roundRect(1.0, 1.0, bandR - 1.0, 6.4, 0.9) + '" fill="' + bandFill + '"/>');
    var hl = 2.6;
    if (c.logo) { o.push(logoBox(c, k, 2.0, 1.8, 4.8, 4.8)); hl = 7.6; }
    /* the reward owns the band's right end — and the band's right end moves */
    var rw = Math.min(14.4, bandR - hl - 8.0);
    var hr = bandR - 1.0 - (c.reward ? rw + 1.0 : 0);
    if (c.hdr) o.push(txt((hl + hr) / 2, 5.8, 2.20, bandTxt, c.hdr.toUpperCase(),
      { ls: 0.2, anchor: 'middle', fit: hr - hl - 1.4, hard: true, halo: false, role: 'header' }));
    if (c.reward) o.push(txt(bandR - 1.7, 5.8, 1.30, bandTxt, c.reward.toUpperCase(),
      { ls: 0.12, anchor: 'end', fit: rw, hard: true, halo: false, op: 0.95, role: 'reward' }));
    bandFlash(o, k, 2.6, 6.85, bandR - 1.6);
    var x = 2.8;
    var hero = heroOf(c);
    var win = foilWindow(o, k, 1.8, 8.5, (right + 0.6) - 1.8, 11.2);
    o.push(txt(x, 12.4, 3.60, win, c.name.toUpperCase(),
      { ls: 0.05, fit: right - x, hard: true, role: 'name' }));
    o.push(txt(x, 18.2, hero.big ? 4.40 : 3.10, win, hero.text,
      { fit: right - x, hard: false, role: hero.key || 'hero' }));
    stack(o, k, details(c, k, hero.key),
      [{ x: x, y: 21.4, size: 1.85, fit: right - x },
       { x: x, y: 23.6, size: 1.50, fit: right - x }]);
    if (k.hasQr) qrPatch(o, k, k.plate ? 47.4 : 44.6, 8.4, k.plate ? 12.6 : 15.4);
  }

  /* L4L · CARD_LG — a name plate left, the contact routes and the code right.
     The plate is NOT an ink slab: on open stock (and on a tint) it is a 13%
     wash of the customer's accent held by a cut-corner ink keyline, so the
     rainbow runs straight through the biggest shape on the label. Only a
     ground already flooded with ink gets the solid bare-foil plate — there the
     plate IS the foil. Either way the type on it is INK. */
  function drawCardLg(o, c, k) {
    /* PLATE, right column: the details, the code and its printed fallback.
       The best of the eight for an artwork ground and the only one with a
       gutter — 0.9 mm of open picture between the name plate's 27.0 and this
       panel's 27.9. With a code the panel runs to the die; without one on an
       artwork ground it stops at 46.9 and the right 15.5 mm is picture, with
       another 3.9 mm band under it. */
    var wide = wideCol(k);
    plateBox(o, k, 27.9, 4.35, (k.hasQr || wide) ? 62.1 : 46.9,
             k.hasQr ? 25.0 : 20.5);
    var plate = cutRect(1.0, 1.0, 26.0, 23.4, 1.6), plateTxt = INK;
    if (k.dark || k.plate) {
      /* the name plate IS the plate here: an ink flood would hide the ground
         on a dark option and a 13% accent wash would go muddy over artwork,
         so both cases print the panel as bare foil and set their type in ink */
      o.push('<path d="' + plate + '" fill="' + FOIL + '"' +
        (k.plate ? ' data-holo="1" data-lt-plate="1"' : '') + '/>');
      /* the name plate is a plate like any other, so the picture runs through
         it too — screenArt is a no-op on the dark options, which have no
         artwork to screen */
      screenArt(o, k, plate);
    } else {
      o.push('<path d="' + plate + '" fill="' + c.accent + '" fill-opacity="0.13"/>');
      o.push('<path d="' + plate + '" fill="none" stroke="' + INK +
        '" stroke-width="0.2" stroke-opacity="0.8"/>');
    }
    var pcx = 14.0, hy = 5.4, ny = 13.0;
    if (c.logo) { o.push(logoBox(c, k, pcx - 2.0, 2.0, 4.0, 4.0)); hy = 8.6; ny = 14.6; }
    if (c.hdr) {
      var lines = c.logo ? [c.hdr] : split2(c.hdr, 18);
      for (var i = 0; i < lines.length; i++) {
        o.push(txt(pcx, hy + i * 1.9, 1.50, plateTxt, lines[i].toUpperCase(),
          { ls: 0.14, anchor: 'middle', fit: 23.0, hard: true, halo: false, role: 'header' }));
      }
    }
    o.push(txt(pcx, ny, 3.40, plateTxt, c.name.toUpperCase(),
      { anchor: 'middle', ls: 0.05, fit: 23.0, hard: true, halo: false, role: 'name' }));
    if (c.reward) {
      /* the same ink-band-with-foil-knockout strip the other layouts use — on
         the foil plate it is ink, on the wash it is the accent */
      o.push('<path d="' + roundRect(2.6, 18.9, 22.8, 2.6, 0.5) + '" fill="' +
        (k.dark ? INK : c.accent) + '"/>');
      o.push(txt(pcx, 20.75, 1.50, FOIL, c.reward.toUpperCase(),
        { anchor: 'middle', ls: 0.12, fit: 21.6, hard: true, halo: false, role: 'reward' }));
    }
    var x = 28.6, right = wide ? 61.4 : 46.2;
    var hero = heroOf(c);
    o.push(txt(x, 8.4, hero.big ? 4.20 : 3.00, k.TXT, hero.text,
      { fit: right - x, hard: false, role: hero.key || 'hero' }));
    stack(o, k, details(c, k, hero.key),
      [{ x: x, y: 12.4, size: 1.85, fit: right - x },
       { x: x, y: 15.0, size: 1.55, fit: right - x },
       { x: x, y: 17.2, size: 1.55, fit: right - x },
       { x: x, y: 19.4, size: 1.55, fit: right - x }]);
    if (k.hasQr) {
      /* 12.6 mm of patch leaves room for a two-line printed fallback under it.
         At EC-M a 64-byte payload is a version 5 code (37 modules), so the
         drawn pitch is 12.6/39 = 0.32 mm — comfortably above the 0.264 mm
         floor this shop has measured on holographic stock. */
      var qx = 47.6, qw = 12.6, qcx = qx + qw / 2;
      if (k.caption) micro(o, k, qcx, 6.8, k.caption, k.TXT,
        { anchor: 'middle', fit: 14.2, op: 0.7, role: 'qr-caption' });
      qrPatch(o, k, qx, 7.6, qw);
      drawFallback(o, k, qcx, 21.6, 1.30, 14.6);
    }
  }

  /* ═══ THE CLEAN SET — owner 2026-09-30 ══════════════════════════════════
     "make them look better, more organized layouts", "for both label sizes",
     "use less ink no dark bgs", "also allow vertical new layouts",
     "orientation switching".

     ONE visual system on both dies and in both orientations, so every label
     reads the same way at a glance:
       · a small tracked HEADER in the accent over one accent hairline;
       · the NAME in bold caps, the NUMBER the biggest thing on the label;
       · details as quiet regular-weight rows, paired (email · @handle on one
         row, the two address lines on one row) when the pair fits the column
         and one per row when it does not;
       · the reward as a HAIRLINE CHIP — an outline and accent wording, no
         fill. The solid bar it replaces was the heaviest ink on the label
         after a flood;
       · the code in a column of its own, and NO SECOND COPY OF THE NUMBER
         under it. When the code's destination already prints in type (the
         phone is the hero, the email is a detail row) that type IS the
         printed fallback; the fallback line only prints under the patch when
         it would be the one place a finder could read where the code goes
         (a web address, a different handle) — copy spec §4d still holds.
     On the default open-foil ground the ink is the type and three hairlines.

     NEW KEYS, never re-drawn old ones. A design record stores `la`, so
     changing what 'callout' draws would change every reprint and every order
     in flight. The ten earlier styles stay renderable exactly as they were and
     are `hidden` from the picker only (pickerLayouts).

     `orient:'v'` is a VERTICAL style. The die the press cuts is still
     landscape; the composition is authored on a portrait canvas and rotated
     into it — the drawStrip mechanism, same transform, same print-safe clamp —
     so the label reads upright when it is applied on end. The builder turns
     its preview upright; the press, the cart and Label Farm see the die as it
     is cut. */

  /* ROUND 2 (owner 2026-10-02, "the text is such a thin light text"): the
     first prints (ss1153, ss1155) set the detail rows in regular weight at 90%
     ink and the code caption at 75%, which on holo foil reads faint. Every row
     is now bold at full ink, a step larger, and the headers a step up. */
  /* the accent where it can be seen: on a screen or an ink ground the accent
     would sit on ink, so the furniture takes the type's own colour there */
  function t2Acc(c, k) { return k.grounded ? k.TXT : c.accent; }
  /* a rough Helvetica advance in mm — only to decide whether two details can
     share a row. The fit pass still measures the real run. */
  function t2Est(s, size) { return String(s || '').length * size * 0.6; }
  /* two lines out of one run, at the most natural break (see towerDraw) */
  function t2Wrap(s) {
    s = String(s || '');
    var at = s.indexOf('@');
    if (at > 0 && /\S@\S/.test(s)) return [s.slice(0, at), s.slice(at)];
    if (s.indexOf(' ') > 0) {
      var sp = split2(s, Math.ceil(s.length / 2));
      if (sp.length === 2) return sp;
    }
    var mid = s.length / 2, best = -1;
    for (var i = 1; i < s.length - 1; i++) {
      if ('._-'.indexOf(s.charAt(i)) >= 0 && (best < 0 || Math.abs(i - mid) < Math.abs(best - mid))) best = i;
    }
    if (best < 0) best = Math.floor(mid);
    return [s.slice(0, best), s.slice(best)];
  }

  /* the tracked header in the accent */
  function t2Head(o, c, k, x, y, size, fitw, anchor) {
    if (!c.hdr) return;
    o.push(txt(x, y, size, t2Acc(c, k), c.hdr.toUpperCase(),
      { ls: 0.18, anchor: anchor, fit: fitw, hard: true, role: 'header' }));
  }
  function t2Rule(o, c, k, x1, y, x2) {
    line(o, x1, y, x2, y, t2Acc(c, k), 0.85, 0.15);
  }

  /* THE DETAIL ROWS, grouped. details() has already filtered to what this
     layout carries and promoted the code's own destination to the front; this
     pairs email with the handle and addr1 with addr2 when the pair fits one
     row at the row's own size, and keeps them apart when it does not. */
  var T2_PAIR = { email: ['social', '  ·  '], social: ['email', '  ·  '],
                  addr1: ['addr2', ', '], addr2: ['addr1', ', '] };
  function t2Rows(c, k, skipKey, fitw, size) {
    var it = details(c, k, skipKey), by = {}, used = {}, out = [];
    for (var i = 0; i < it.length; i++) by[it[i].key] = it[i];
    for (var j = 0; j < it.length; j++) {
      var a = it[j];
      if (used[a.key]) continue;
      used[a.key] = 1;
      var p = T2_PAIR[a.key], b = p && by[p[0]];
      if (b && !used[b.key]) {
        /* address lines always read addr1 first, whichever was promoted */
        var first = (a.key === 'addr2') ? b : a, second = (first === a) ? b : a;
        var joined = first.text + p[1] + second.text;
        if (t2Est(joined, size) <= fitw) {
          used[b.key] = 1;
          out.push({ key: first.key, text: joined, keys: [first.key, second.key],
                     parts: [first.text, second.text] });
          continue;
        }
      }
      out.push({ key: a.key, text: a.text, keys: [a.key], parts: [a.text] });
    }
    return out;
  }
  /* the texts the first n rows will print — for the fallback decision */
  function t2Printed(groups, n) {
    var out = [];
    for (var i = 0; i < groups.length && i < n; i++) out = out.concat(groups[i].parts);
    return out;
  }
  /* print the rows that have a slot; report every field in the rest */
  function t2Place(o, k, groups, rows) {
    var i = 0;
    for (; i < groups.length && i < rows.length; i++) {
      var g = groups[i], rw = rows[i];
      o.push(txt(rw.x, rw.y, rw.size, k.TXT, g.text,
        { anchor: rw.anchor, fit: rw.fit, hard: true, role: g.key }));
    }
    for (; i < groups.length; i++) {
      for (var j = 0; j < groups[i].keys.length; j++) _dropped.push(groups[i].keys[j]);
    }
  }
  /* does the code need its own printed fallback, or is its destination
     already on the label in type? */
  function t2NeedFb(k, printed) {
    if (!k.hasQr || !k.fallback) return false;
    var ln = fallbackLines(k);
    return printed.indexOf(ln[ln.length - 1]) < 0;
  }
  function t2FbHeight(k, size) {
    return fallbackLines(k).length > 1 ? size * 1.38 + size * 1.12 : size * 1.12;
  }

  /* THE REWARD CHIP — a hairline pill with the wording in the accent. It is
     authored at the widest it may be and the fit pass HUGS it to the measured
     wording (fit(): rect[data-lt-hug]), so a short reward gets a short chip
     without this file guessing glyph widths. A consumer that never runs the
     fit pass still gets a valid chip, just a wider one. */
  function t2Chip(o, c, k, x, y, maxw, h, size, anchor) {
    if (!c.reward) return;
    var col = t2Acc(c, k), id = k.id + 'h' + (++_plateN), pad = r2(h * 0.6);
    var x0 = anchor === 'middle' ? x - maxw / 2 : (anchor === 'end' ? x - maxw : x);
    o.push('<rect x="' + r2(x0) + '" y="' + r2(y) + '" width="' + r2(maxw) +
      '" height="' + r2(h) + '" rx="' + r2(h / 2) + '" fill="none" stroke="' + col +
      '" stroke-width="0.2" data-lt-hug="' + id + '" data-hug-at="' + (anchor || 'start') +
      '" data-hug-pad="' + pad + '"/>');
    o.push(txt(x0 + maxw / 2, y + h / 2 + size * 0.355, size, col, c.reward.toUpperCase(),
      { anchor: 'middle', ls: 0.12, fit: maxw - 2 * pad, hard: true, halo: false,
        role: 'reward' }).replace('<text ', '<text data-lt-hugged="' + id + '" '));
  }

  /* ── HORIZONTAL · "CONTACT CARD" ──────────────────────────────────────
     Text column left, code column right with a hairline between them. No code
     and the column reflows to the full width (on an artwork ground the freed
     width stays picture, wideCol). Numbers are hand-set per die. */
  var LINE_G = {
    standard_177x075: {
      x: 2.5, colR: 28.9, openR: 42.45, artR: 30.4,
      hy: 3.75, hs: 1.20, ry: 4.6, ny: 8.15, ns: 2.55, py: 12.1, ps: 3.35, psSmall: 2.4,
      rows: [[14.75, 1.45], [16.75, 1.35]], rowsR: [[14.75, 1.45]],
      chip: [15.7, 1.85, 1.0], logo: [1.75, 2.8],
      qR: 42.95, qw: 12.4, capY: 3.35, qwFb: 10.8, capYFb: 2.9, capGap: 0.55,
      fbY: 15.85, fbS: 1.05, seam: [3.0, 16.3], plate: [1.2, 1.3, 18.0],
      /* WITH A LOGO (2026-10-02): the mark leads the header row at 5.0 mm and
         everything under it steps down 0.8-1.1 mm to make the room */
      withLogo: { logo: [1.6, 5.0], logoGap: 0.8, hy: 4.4, ry: 5.3, ny: 9.2, ns: 2.4,
                  py: 12.9, ps: 3.1, psSmall: 2.25,
                  rows: [[15.3, 1.40], [17.2, 1.30]], rowsR: [[15.3, 1.40]],
                  chip: [16.05, 1.6, 0.95] }
    },
    large_250x100: {
      x: 3.0, colR: 42.2, openR: 60.5, artR: 44.0,
      hy: 5.0, hs: 1.50, ry: 6.0, ny: 10.6, ns: 3.1, py: 15.5, ps: 4.1, psSmall: 3.0,
      rows: [[18.6, 1.75], [20.85, 1.65], [23.0, 1.50]], rowsR: [[18.5, 1.70], [20.6, 1.60]],
      chip: [21.6, 2.1, 1.1], logo: [2.4, 3.6],
      qR: 60.7, qw: 16.2, capY: 4.6, qwFb: 14.2, capYFb: 3.9, capGap: 0.6,
      fbY: 20.6, fbS: 1.30, seam: [4.0, 21.4], plate: [1.2, 1.3, 24.1],
      withLogo: { logo: [2.2, 7.5], logoGap: 1.0, hy: 6.5, ry: 7.6, ny: 13.4, ns: 3.0,
                  py: 18.2, ps: 3.9, psSmall: 2.9,
                  rows: [[21.3, 1.60], [23.3, 1.45]], rowsR: [[21.3, 1.60]],
                  chip: [22.05, 1.9, 1.05] }
    }
  };
  /* a geometry with one composition's overrides laid over it */
  function t2With(a, b) {
    var o = {}, kk;
    for (kk in a) if (Object.prototype.hasOwnProperty.call(a, kk)) o[kk] = a[kk];
    for (kk in b) if (Object.prototype.hasOwnProperty.call(b, kk)) o[kk] = b[kk];
    return o;
  }
  function drawLine(o, c, k) {
    var G = LINE_G[k.SZ.id] || LINE_G.standard_177x075, wide = wideCol(k);
    if (c.logo && G.withLogo) G = t2With(G, G.withLogo);
    var x = G.x, right = k.hasQr ? G.colR : (wide ? G.openR : G.artR);
    var hero = heroOf(c);
    var slots = c.reward ? G.rowsR : G.rows;
    var groups = t2Rows(c, k, hero.key, right - x, slots[0][1]);
    var fb = t2NeedFb(k, [hero.text].concat(t2Printed(groups, slots.length)));
    plateBox(o, k, G.plate[0], G.plate[1],
             (k.hasQr || wide) ? k.W - G.plate[0] : right + 0.9, G.plate[2]);
    var hx = x;
    if (c.logo) { o.push(logoBox(c, k, x, G.logo[0], G.logo[1], G.logo[1])); hx = x + G.logo[1] + (G.logoGap || 0.6); }
    t2Head(o, c, k, hx, G.hy, G.hs, right - hx);
    t2Rule(o, c, k, hx, G.ry, right);
    o.push(txt(x, G.ny, G.ns, k.TXT, c.name.toUpperCase(),
      { ls: 0.04, fit: right - x, hard: true, role: 'name' }));
    o.push(txt(x, G.py, hero.big ? G.ps : G.psSmall, k.TXT, hero.text,
      { fit: right - x, hard: false, role: hero.key || 'hero' }));
    t2Place(o, k, groups, slots.map(function (s) {
      return { x: x, y: s[0], size: s[1], fit: right - x };
    }));
    t2Chip(o, c, k, x, G.chip[0], right - x, G.chip[1], G.chip[2], 'start');
    if (k.hasQr) {
      var qw = fb ? G.qwFb : G.qw, qx = G.qR - qw, qcx = qx + qw / 2;
      var cy = fb ? G.capYFb : G.capY, sx = (right + qx) / 2;
      line(o, sx, G.seam[0], sx, G.seam[1], k.TXT, 0.25, 0.13);
      if (k.caption) micro(o, k, qcx, cy, k.caption, k.TXT,
        { anchor: 'middle', fit: qw + 1.0, op: 1, role: 'qr-caption' });
      qrPatch(o, k, qx, cy + G.capGap, qw);
      if (fb) drawFallback(o, k, qcx, G.fbY, G.fbS, qw + 1.4);
    }
  }

  /* ── HORIZONTAL · "CENTERED" ──────────────────────────────────────────
     No code: the whole label is type, centred on the die's own axis, the
     header over a short centred rule. The "everything in type" style. */
  var CENTER_G = {
    standard_177x075: {
      fit: 40.6, hy: 3.8, hs: 1.25, ry: 4.7, rw: 5.5, ny: 8.3, ns: 2.8, py: 12.45, ps: 3.9,
      psSmall: 2.8, rows: [[15.0, 1.45], [16.85, 1.30]], rowsR: [[14.85, 1.40]],
      chip: [15.75, 1.8, 1.0], chipW: 26, logo: [2.2, 1.9, 3.0], plate: [1.2, 1.3, 18.0],
      logoL: [2.4, 7.0, 1.2, 2.4]    /* x, size, gap to the type, right margin */
    },
    large_250x100: {
      fit: 58.5, hy: 4.9, hs: 1.55, ry: 5.95, rw: 7.0, ny: 11.0, ns: 3.5, py: 16.4, ps: 4.8,
      psSmall: 3.4, rows: [[19.6, 1.75], [21.75, 1.60], [23.5, 1.40]], rowsR: [[19.5, 1.70]],
      chip: [20.7, 2.3, 1.15], chipW: 34, logo: [2.6, 2.4, 3.8], plate: [1.2, 1.3, 24.1],
      logoL: [3.0, 9.5, 1.5, 3.0]
    }
  };
  function drawCenter(o, c, k) {
    var G = CENTER_G[k.SZ.id] || CENTER_G.standard_177x075, cx = k.W / 2, fit = G.fit;
    var hero = heroOf(c);
    var slots = c.reward ? G.rowsR : G.rows;
    plateBox(o, k, G.plate[0], G.plate[1], k.W - G.plate[0], G.plate[2]);
    if (c.logo) {
      /* the mark gets a column of its own, vertically centred, and the type
         centres on the band to its right */
      var L = G.logoL, tl = L[0] + L[1] + L[2], tr = k.W - L[3];
      o.push(logoBox(c, k, L[0], (k.H - L[1]) / 2, L[1], L[1]));
      cx = (tl + tr) / 2;
      fit = tr - tl;
    }
    var groups = t2Rows(c, k, hero.key, fit, slots[0][1]);
    t2Head(o, c, k, cx, G.hy, G.hs, fit, 'middle');
    if (c.hdr) t2Rule(o, c, k, cx - G.rw, G.ry, cx + G.rw);
    o.push(txt(cx, G.ny, G.ns, k.TXT, c.name.toUpperCase(),
      { anchor: 'middle', ls: 0.04, fit: fit, hard: true, role: 'name' }));
    o.push(txt(cx, G.py, hero.big ? G.ps : G.psSmall, k.TXT, hero.text,
      { anchor: 'middle', fit: fit, hard: false, role: hero.key || 'hero' }));
    t2Place(o, k, groups, slots.map(function (s) {
      return { x: cx, y: s[0], size: s[1], fit: fit, anchor: 'middle' };
    }));
    t2Chip(o, c, k, cx, G.chip[0], Math.min(G.chipW, fit), G.chip[1], G.chip[2], 'middle');
  }

  /* ── VERTICAL · "VERTICAL" and "VERTICAL · NAME FIRST" ────────────────
     Authored on the portrait canvas (die short side wide, long side tall) and
     rotated into the landscape cell the press cuts, exactly as drawStrip is.
     A FLOW, not fixed rows: the blocks stack top to bottom at hand-set sizes
     with hand-set minimum gaps, as many detail rows as the height allows are
     kept, and whatever height is left is shared between the major gaps (each
     capped) so a short design does not float at the top of a tall label.
     codeFirst: header, code, name, number, details, reward.
     name first: header, name, number, details, reward, code. */
  var TOWER_G = {
    standard_177x075: {
      pad: 1.8, top: 2.1, hs: 1.1, hLead: 1.5, hSplit: 16, ruleW: 3.0,
      qw: 14.0, qwFb: 12.4, capGap: 0.45, fbS: 1.05,
      ns: 2.2, nsOpen: 2.7, nSplit: 12, ps: 2.6, psOpen: 3.0, rs: 1.25, rLead: 1.75,
      chipH: 1.9, chipS: 0.95, logo: 7.0, gap: 1.0, tight: 0.55, maxGap: 2.2
    },
    large_250x100: {
      pad: 2.2, top: 2.6, hs: 1.4, hLead: 1.9, hSplit: 18, ruleW: 4.0,
      qw: 19.0, qwFb: 17.0, capGap: 0.55, fbS: 1.3,
      ns: 3.0, nsOpen: 3.5, nSplit: 13, ps: 3.4, psOpen: 4.0, rs: 1.6, rLead: 2.15,
      chipH: 2.4, chipS: 1.2, logo: 9.5, gap: 1.3, tight: 0.7, maxGap: 3.0
    }
  };
  function towerDraw(o, c, k, codeFirst) {
    var T = TOWER_G[k.SZ.id] || TOWER_G.standard_177x075;
    var PW = k.H, PH = k.W, cx = PW / 2, fw = PW - 2 * T.pad;
    var g = ['<g transform="translate(0 ' + r2(k.H) + ') rotate(-90)">'];
    /* the plate rides the rotation; its screen is handed the exact inverse of
       the group transform so the artwork ghosts through in DIE space */
    /* asked for past the print-safe line and clamped back onto it: a 19 mm
       run has no width to give a plate a margin of its own */
    plateBox(g, k, 0.5, 0.5, PW - 0.5, PH - 0.5, PW, PH,
             'rotate(90) translate(0 ' + r2(-k.H) + ')');
    var q = k.hasQr, hero = heroOf(c);
    var hdrL = c.hdr ? split2(c.hdr.toUpperCase(), T.hSplit) : [];
    var nameL = c.name ? split2(c.name.toUpperCase(), T.nSplit) : [];
    var ns = q ? T.ns : T.nsOpen;
    var ps = (q ? T.ps : T.psOpen) * (hero.big ? 1 : 0.8);
    var capS = Math.max(MIN_TEXT, k.SZ.micro);
    var groups = t2Rows(c, k, hero.key, fw, T.rs);
    /* A ROW TOO LONG FOR THE COLUMN WRAPS. A 19 mm run cannot hold a 38-
       character email at a readable size, and the fit pass would otherwise
       shrink it to the floor and then drop characters - which prints a wrong
       address. So a row that would have to shrink past 85% of its size is set
       on two lines instead: an email breaks before its '@', other text at the
       space nearest its middle, and a run with no space at the '.', '_' or '-'
       nearest its middle (a long handle), else at the middle. */
    for (var gi = 0; gi < groups.length; gi++) {
      var gt = groups[gi].text;
      groups[gi].lines = (t2Est(gt, T.rs * 0.85) > fw) ? t2Wrap(gt) : [gt];
    }
    function rowLines(n) {
      var t = 0;
      for (var i = 0; i < n && i < groups.length; i++) t += groups[i].lines.length;
      return t;
    }

    /* one block = its height and how to draw it at a top y. `flex` marks a gap
       that may grow; the others hold their minimum. */
    function blocks(fb, nRows) {
      var B = [];
      function add(h, gap, flex, draw) { B.push({ h: h, gap: gap, flex: flex, draw: draw }); }
      if (c.logo) add(T.logo, T.tight, false, function (y) {
        g.push(logoBox(c, k, cx - T.logo / 2, y, T.logo, T.logo));
      });
      if (hdrL.length) {
        add((hdrL.length - 1) * T.hLead + 1.12 * T.hs, T.tight, false, function (y) {
          for (var i = 0; i < hdrL.length; i++) {
            g.push(txt(cx, y + 0.905 * T.hs + i * T.hLead, T.hs, t2Acc(c, k), hdrL[i],
              { ls: 0.16, anchor: 'middle', fit: fw, hard: true, role: 'header' }));
          }
        });
        add(0.2, T.gap, true, function (y) { t2Rule(g, c, k, cx - T.ruleW, y + 0.1, cx + T.ruleW); });
      }
      function code() {
        if (!q) return;
        var qw = fb ? T.qwFb : T.qw;
        var h = 1.12 * capS + T.capGap + qw + (fb ? 0.5 + t2FbHeight(k, T.fbS) : 0);
        add(h, T.gap, true, function (y) {
          if (k.caption) micro(g, k, cx, y + 0.905 * capS, k.caption, k.TXT,
            { anchor: 'middle', fit: fw + 0.4, op: 1, role: 'qr-caption' });
          var py = y + 1.12 * capS + T.capGap;
          qrPatch(g, k, (PW - qw) / 2, py, qw);
          if (fb) drawFallback(g, k, cx, py + qw + 0.5 + 0.905 * T.fbS, T.fbS, fw);
        });
      }
      if (codeFirst) code();
      if (nameL.length) add((nameL.length - 1) * ns * 1.12 + 1.12 * ns, T.tight, false, function (y) {
        for (var i = 0; i < nameL.length; i++) {
          g.push(txt(cx, y + 0.905 * ns + i * ns * 1.12, ns, k.TXT, nameL[i],
            { anchor: 'middle', ls: 0.04, fit: fw, hard: true, role: 'name' }));
        }
      });
      if (hero.text) add(1.12 * ps, T.gap, true, function (y) {
        g.push(txt(cx, y + 0.905 * ps, ps, k.TXT, hero.text,
          { anchor: 'middle', fit: fw, hard: false, role: hero.key || 'hero' }));
      });
      if (nRows > 0) add((rowLines(nRows) - 1) * T.rLead + 1.12 * T.rs, T.gap, true, function (y) {
        var li = 0;
        for (var i = 0; i < groups.length; i++) {
          if (i >= nRows) {
            for (var j = 0; j < groups[i].keys.length; j++) _dropped.push(groups[i].keys[j]);
            continue;
          }
          for (var m = 0; m < groups[i].lines.length; m++, li++) {
            g.push(txt(cx, y + 0.905 * T.rs + li * T.rLead, T.rs, k.TXT, groups[i].lines[m],
              { anchor: 'middle', fit: fw, hard: true, role: groups[i].key }));
          }
        }
      });
      if (c.reward) add(T.chipH, T.gap, true, function (y) {
        t2Chip(g, c, k, cx, y, fw, T.chipH, T.chipS, 'middle');
      });
      if (!codeFirst) code();
      if (B.length) B[B.length - 1].gap = 0;
      return B;
    }
    function total(B) {
      var t = 0;
      for (var i = 0; i < B.length; i++) t += B[i].h + B[i].gap;
      return t;
    }
    var avail = PH - 2 * T.top;
    /* as many rows as fit, first without a printed fallback; if the rows that
       survive do not print the code's destination, plan again with one */
    var nRows = groups.length, fb = false, B;
    for (var pass = 0; pass < 2; pass++) {
      nRows = groups.length;
      while (nRows > 0 && total(blocks(fb, nRows)) > avail) nRows--;
      var need = t2NeedFb(k, [hero.text].concat(t2Printed(groups, nRows)));
      if (need === fb) break;
      fb = need;
    }
    B = blocks(fb, nRows);
    if (nRows === 0 && groups.length) t2Place(g, k, groups, []);   /* report them */
    var left = Math.max(0, avail - total(B)), nFlex = 0;
    for (var f = 0; f < B.length; f++) if (B[f].flex && B[f].gap > 0) nFlex++;
    var extra = nFlex ? Math.min(T.maxGap, left / nFlex) : 0;
    var y = T.top + (left - extra * nFlex) / 2;
    for (var b = 0; b < B.length; b++) {
      B[b].draw(y);
      y += B[b].h + B[b].gap + (B[b].flex && B[b].gap > 0 ? extra : 0);
    }
    g.push('</g>');
    o.push(g.join(''));
  }
  function drawTower(o, c, k) { towerDraw(o, c, k, true); }
  function drawTowerName(o, c, k) { towerDraw(o, c, k, false); }

  var DRAW = {
    photo: drawPhoto,
    callout: drawCallout, plain: drawPlain, banner: drawBanner, strip: drawStrip,
    photo_lg: drawPhotoLg,
    callout_lg: drawCalloutLg, plain_lg: drawPlainLg, banner_lg: drawBannerLg,
    card_lg: drawCardLg,
    line: drawLine, center: drawCenter, tower: drawTower, tower_name: drawTowerName,
    line_lg: drawLine, center_lg: drawCenter, tower_lg: drawTower,
    tower_name_lg: drawTowerName
  };

  /* ── the SVG ──────────────────────────────────────────────────────────
     viewBox is always "0 0 longMm shortMm" for the die, so a host of the
     die's pixel width rasters to exactly pxLong x pxShort with no rescale. */
  function svg(cfg, sizeId) {
    var SZ = sizeOf(pick(sizeId, (cfg || {}).size_id, (cfg || {}).sizeId, (cfg || {}).size));
    var c = norm(cfg, SZ.id);
    var lay = layOf(c.layout, SZ.id);
    var bg = bgOf(c.bg);
    var W = SZ.longMm, H = SZ.shortMm;
    var id = 'lt' + (++_n);
    var dark = bg.dark;
    var TXT = dark ? FOIL : INK;            /* #ffffff = bare foil, never white ink */
    _HALO = dark ? INK : FOIL;              /* always the opposite polarity */
    /* on a translucent tint the halo is bare foil laid on a screened ground —
       visible, unlike on open stock. Lighter and softer there, or every detail
       row prints with a chalk outline round it. */
    _HALO_W = (!dark && bg.tint) ? 0.08 : 0.15;
    _HALO_SO = (!dark && bg.tint) ? 0.4 : 0;
    var MUT = dark ? FOIL : INK;

    _dropped = [];
    _plateN = 0;
    var payload = lay.qr ? qrPayload(c) : '';
    if (payload && lay.qrActions && lay.qrActions.indexOf(c.qa) < 0) {
      /* the layout cannot print this code's destination in type, so it may not
         carry the code at all (copy spec §4d rule 6). The builder refuses this
         combination up front; the renderer refuses it again here rather than
         printing a label whose only route is unreadable. */
      _dropped.push('qr');
      payload = '';
    }
    var k = {
      W: W, H: H, M: SZ.M, SZ: SZ, id: id, dark: dark, TXT: TXT, MUT: MUT,
      grounded: grounded(bg),
      /* an ARTWORK ground asks every layout for a bare-foil plate under its
         type. Nothing else turns it on: a drawn ground composes for the stock
         and a plate would cover the very furniture it exists to show. */
      plate: !!(bg.kind === 'art' && bg.image && bg.plate),
      /* the artwork's href, for the SECOND draw of it that screens back
         through every plate (screenArt). Empty on every other ground. */
      art: (bg.kind === 'art' && bg.image) ? String(bg.image) : '',
      accent: c.accent, carries: lay.carries || [],
      hasQr: !!payload, payload: payload,
      caption: payload && lay.qrCaption !== false ? qrCaption(c) : '',
      fallback: payload ? qrFallback(c) : ''
    };

    var o = [];
    o.push('<svg viewBox="0 0 ' + W + ' ' + H + '" xmlns="http://www.w3.org/2000/svg" ' +
      'data-lt="1" data-lt-size="' + SZ.id + '" data-lt-layout="' + lay.key + '" ' +
      'data-lt-bg="' + esc(c.bg) + '" data-lt-ns="' + id + '">');
    o.push('<defs>');
    o.push('<linearGradient id="' + id + 'f" x1="0" y1="0" x2="1" y2="1">' +
      '<stop offset="0" stop-color="#ffffff"/><stop offset="0.5" stop-color="#ffffff" stop-opacity="0.92"/>' +
      '<stop offset="1" stop-color="#ffffff"/></linearGradient>');
    o.push('<linearGradient id="' + id + 'p" x1="0" y1="0" x2="1" y2="1">' +
      '<stop offset="0" stop-color="#101830"/><stop offset="0.55" stop-color="#04050a"/>' +
      '<stop offset="1" stop-color="#241335"/></linearGradient>');
    /* clip to the FULL die — a clip inset from the cut re-imposes the very
       margin a full-bleed flood is there to remove */
    o.push('<clipPath id="' + id + 'c"><path d="' + roundRect(0, 0, W, H, SZ.corner) + '"/></clipPath>');
    o.push('</defs>');

    o.push('<g clip-path="url(#' + id + 'c)">');
    function flood(fill, extra) {
      o.push('<path d="' + roundRect(0, 0, W, H, SZ.corner) + '" fill="' + fill + '"' +
        (extra || '') + '/>');
    }
    if (bg.kind === 'art' && bg.image) {
      /* ARTWORK: the raster bleeds to the die and nothing floods under it.
         Same call ss-seal.js:1866-1869 makes for a Seal Studio background,
         same reason — a background clipped to an inset plate leaves "a padded
         border of nothing" all round the cut. 'slice' is COVER: the die crops
         the image, it is never squashed to fit, so the 2.36:1 landscape file
         gives up 2.7% of its long edge on the 2.5:1 large die instead of
         stretching. The type that follows lands on a bare-foil plate. */
      o.push('<image href="' + esc(bg.image) + '" x="0" y="0" width="' + r2(W) +
        '" height="' + r2(H) + '" preserveAspectRatio="xMidYMid slice"/>');
    } else if (c.bg === 'black') flood(INK);
    else if (c.bg === 'premium') flood('url(#' + id + 'p)');
    else if (bg.tint) {
      /* a SCREEN, never a flood: 0.35-0.55 of one deep hue, so the rainbow
         underneath keeps shifting and the type on top stays ink */
      flood(bg.tint, ' fill-opacity="' +
        Math.max(0.05, Math.min(0.6, +bg.op || 0.45)) + '"');
    } else if (c.bg === 'silver') {
      o.push('<rect width="' + W + '" height="' + H + '" fill="' + INK + '" fill-opacity="0.16"/>');
    } else if (c.bg === 'prismatic') {
      /* faceted burst: ink wedges radiating from beyond the corner, bare foil
         flashing between them — the refractor look built from the material */
      var fx = -4, fy = H + 6, FR = W * 1.45;
      var seg = [[-82, -74, 0.10], [-71, -62, 0.05], [-58, -52, 0.12], [-49, -40, 0.06],
                 [-37, -31, 0.10], [-28, -20, 0.05], [-16, -9, 0.11], [-6, -1, 0.06]];
      for (var pi = 0; pi < seg.length; pi++) {
        var a1 = seg[pi][0] * Math.PI / 180, a2 = seg[pi][1] * Math.PI / 180;
        o.push('<path d="M' + r2(fx) + ' ' + r2(fy) +
          'L' + r2(fx + FR * Math.cos(a1)) + ' ' + r2(fy + FR * Math.sin(a1)) +
          'L' + r2(fx + FR * Math.cos(a2)) + ' ' + r2(fy + FR * Math.sin(a2)) +
          'Z" fill="' + INK + '" opacity="' + seg[pi][2] + '"/>');
      }
    }
    /* 'holo' floods nothing: the foil IS the ground */

    /* ── background art ────────────────────────────────────────────────
       A registered ground draws its own furniture HERE: after the flood or
       the screen, before a single glyph. That order is the contract — art
       that ran later would print on top of the customer's phone number.
       It is handed the die in mm, this mount's id namespace (every id it
       mints must start with it), the ground's polarity and the customer's
       clamped accent, and it returns an SVG string. A throwing or malformed
       art function costs its furniture, never the label. */
    if (typeof bg.art === 'function') {
      try {
        var art = bg.art(W, H, id, dark, c.accent);
        if (art) o.push(String(art));
      } catch (_) {}
    }

    (DRAW[lay.key] || drawCallout)(o, c, k);
    o.push('</g>');
    /* one confident keyline against the cut */
    o.push('<path d="' + roundRect(SAFE, SAFE, W - 2 * SAFE, H - 2 * SAFE, SZ.corner - 0.4) +
      '" fill="none" stroke="' + (dark ? FOIL : INK) + '" stroke-width="0.18" opacity="' +
      (dark ? 0.55 : 0.75) + '"/>');
    o.push('</svg>');
    var out = o.join('');
    if (_dropped.length) {
      out = out.replace('data-lt="1"', 'data-lt="1" data-lt-dropped="' +
        esc(_dropped.filter(function (v, i, a) { return a.indexOf(v) === i; }).join(',')) + '"');
    }
    return out;
  }

  /* ── fit pass ─────────────────────────────────────────────────────────
     Shrinks any text[data-fit] wider than its cap, letter-spacing with it, and
     floors at MIN_TEXT. Runs after the SVG is in the DOM (getComputedTextLength
     needs layout). A run still over its cap AT the floor loses characters
     rather than crossing the cut line — type at 0.95 mm is small, type past the
     edge is gone. Returns the smallest final size in mm across every text on
     the label, so a caller can refuse a design that went unreadable. */
  function fit(root) {
    var ts = root.querySelectorAll('text[data-fit]');
    for (var i = 0; i < ts.length; i++) {
      var el = ts[i], max = parseFloat(el.getAttribute('data-fit'));
      if (!(max > 0)) continue;
      try {
        var len = el.getComputedTextLength();
        /* a DETACHED or display:none host measures every run as 0 — skipping
           keeps a hidden mount from silently "passing" the readability floor */
        if (!len) continue;
        var hard = el.hasAttribute('data-fit-hard');
        for (var pass = 0; pass < 4 && len > max; pass++) {
          var fs = parseFloat(el.getAttribute('font-size')) || 3;
          var ratio = max / len;
          var next = Math.max(MIN_TEXT, fs * ratio * (hard ? 0.985 : 0.995));
          el.setAttribute('font-size', r2(next));
          var ls = parseFloat(el.getAttribute('letter-spacing')) || 0;
          if (ls) el.setAttribute('letter-spacing', Math.max(0, ls * ratio).toFixed(3));
          if (next <= MIN_TEXT + 0.001) break;
          len = el.getComputedTextLength();
        }
        /* still over at the floor? give up the LETTER-SPACING before a glyph.
           The spacing is decoration; the characters are the information a
           finder acts on, and a caption is the usual offender ('SCAN FOR
           WHATSAPP' spends 16 letter gaps on it). */
        if (el.getComputedTextLength() > max) {
          var ls0 = parseFloat(el.getAttribute('letter-spacing')) || 0;
          if (ls0 > 0) {
            el.setAttribute('letter-spacing', (ls0 * 0.5).toFixed(3));
            if (el.getComputedTextLength() > max) el.setAttribute('letter-spacing', '0');
          }
        }
        var guard = 0;
        while (el.getComputedTextLength() > max && el.textContent.length > 1 && guard++ < 96) {
          el.textContent = el.textContent.slice(0, -1);
          el.setAttribute('data-fit-trim', '1');
        }
      } catch (_) {}
    }
    /* the clean set's reward chip is authored at its widest; shrink-wrap it to
       the wording the pass above settled on (t2Chip) */
    var hugs = root.querySelectorAll('rect[data-lt-hug]');
    for (var h = 0; h < hugs.length; h++) {
      try {
        var hr = hugs[h], hid = hr.getAttribute('data-lt-hug');
        var ht = root.querySelector('text[data-lt-hugged="' + hid + '"]');
        var tl = ht ? ht.getComputedTextLength() : 0;
        if (!tl) continue;
        var pad = parseFloat(hr.getAttribute('data-hug-pad')) || 1;
        var hx0 = parseFloat(hr.getAttribute('x')), hmax = parseFloat(hr.getAttribute('width'));
        var hw = Math.min(hmax, tl + 2 * pad), at = hr.getAttribute('data-hug-at');
        var nx = at === 'middle' ? hx0 + (hmax - hw) / 2 : (at === 'end' ? hx0 + hmax - hw : hx0);
        hr.setAttribute('x', r2(nx));
        hr.setAttribute('width', r2(hw));
        ht.setAttribute('x', r2(nx + hw / 2));
      } catch (_) {}
    }
    var floor = 99, all = root.querySelectorAll('text');
    for (var j = 0; j < all.length; j++) {
      var fsz = parseFloat(all[j].getAttribute('font-size'));
      if (fsz > 0 && String(all[j].textContent || '').length) floor = Math.min(floor, fsz);
    }
    return floor === 99 ? null : floor;
  }

  /* ── print-safe audit ─────────────────────────────────────────────────
     Every text run must sit inside the 1.0 mm hard margin. Measured through
     client rects so a rotated composition (the portrait strip) is checked in
     die space like everything else. Returns the offenders; empty is the pass.
     Used by tools/lt_label_check.py and safe to call from the builder. */
  function safeReport(s) {
    var out = [];
    try {
      var vb = String(s.getAttribute('viewBox') || '').trim().split(/[\s,]+/);
      var W = parseFloat(vb[2]), H = parseFloat(vb[3]);
      var box = s.getBoundingClientRect();
      if (!(box.width > 0 && W > 0)) return out;
      var sx = box.width / W, sy = box.height / H;
      var els = s.querySelectorAll('text');
      for (var i = 0; i < els.length; i++) {
        var r = els[i].getBoundingClientRect();
        if (!(r.width > 0)) continue;
        var x0 = (r.left - box.left) / sx, x1 = (r.right - box.left) / sx;
        var y0 = (r.top - box.top) / sy, y1 = (r.bottom - box.top) / sy;
        var over = Math.max(SAFE - x0, SAFE - y0, x1 - (W - SAFE), y1 - (H - SAFE));
        if (over > 0.02) {
          out.push({ text: String(els[i].textContent || '').slice(0, 40),
                     role: els[i].getAttribute('data-lt-role') || '',
                     over: Math.round(over * 1000) / 1000,
                     box: [Math.round(x0 * 100) / 100, Math.round(y0 * 100) / 100,
                           Math.round(x1 * 100) / 100, Math.round(y1 * 100) / 100] });
        }
      }
    } catch (_) {}
    return out;
  }

  /* ── collision audit ──────────────────────────────────────────────────
     Two runs sharing the same millimetres print as mush, and a fit pass that
     only enforces WIDTH cannot see it. Measured through client rects for the
     same reason safeReport is: the portrait strip is a rotated composition and
     has to be judged in die space. A little overlap of the em boxes is normal
     between stacked lines (an ascender under a descender), so only an
     intersection worth more than an eighth of the smaller run is reported.
     Used by tools/lt_label_check.py; safe to call from the builder. */
  function overlapReport(s) {
    var out = [];
    try {
      var vb = String(s.getAttribute('viewBox') || '').trim().split(/[\s,]+/);
      var W = parseFloat(vb[2]), H = parseFloat(vb[3]);
      var box = s.getBoundingClientRect();
      if (!(box.width > 0 && W > 0)) return out;
      var sx = box.width / W, sy = box.height / H;
      var els = s.querySelectorAll('text'), bs = [];
      for (var i = 0; i < els.length; i++) {
        var r = els[i].getBoundingClientRect();
        if (!(r.width > 0)) continue;
        bs.push({ role: els[i].getAttribute('data-lt-role') || '?',
                  t: String(els[i].textContent || '').slice(0, 24),
                  x0: (r.left - box.left) / sx, x1: (r.right - box.left) / sx,
                  y0: (r.top - box.top) / sy, y1: (r.bottom - box.top) / sy });
      }
      for (var a = 0; a < bs.length; a++) {
        for (var b = a + 1; b < bs.length; b++) {
          var ox = Math.min(bs[a].x1, bs[b].x1) - Math.max(bs[a].x0, bs[b].x0);
          var oy = Math.min(bs[a].y1, bs[b].y1) - Math.max(bs[a].y0, bs[b].y0);
          if (ox <= 0 || oy <= 0) continue;
          var mn = Math.min((bs[a].x1 - bs[a].x0) * (bs[a].y1 - bs[a].y0),
                            (bs[b].x1 - bs[b].x0) * (bs[b].y1 - bs[b].y0));
          if (mn > 0 && (ox * oy) / mn > 0.125) {
            out.push({ a: bs[a].role + ':' + bs[a].t, b: bs[b].role + ':' + bs[b].t,
                       frac: Math.round((ox * oy) / mn * 100) / 100 });
          }
        }
      }
    } catch (_) {}
    return out;
  }

  /* ── PREVIEW FOIL, PRINT WHITE ────────────────────────────────────────
     The stock is silver holographic foil and there is no white ink, so every
     '#ffffff' means "bare foil". Previews sit the SVG on a foil-simulating
     sweep (or an Open Holo label would render as ink floating on the page);
     production passes {print:true} and renders on WHITE, because in the press
     pipeline white pixels = no ink laid = foil showing. The foil sim must
     NEVER reach a print render — it prints as grey ink across the whole label. */
  var _cssDone = false;
  function ensureCss() {
    if (_cssDone) return;
    _cssDone = true;
    var s = document.createElement('style');
    s.textContent = '.lt-foil{background:linear-gradient(115deg,#b9d4ef 0%,#a9e7f1 15%,' +
      '#cbc9f7 31%,#f2c9e6 47%,#f7dfc0 63%,#c3edd8 79%,#bcd0ee 100%);' +
      'border-radius:8px;overflow:hidden}';
    document.head.appendChild(s);
  }
  /* The sim. Deliberately chromatic: the earlier grey-pearl version read as
     blank paper in a thumbnail, and a customer picking a HOLOGRAPHIC label has
     to see the rainbow they are buying. Every stop stays light (L ~0.80-0.88),
     so ink type keeps better than 10:1 over it and the preview never flatters
     a design the press could not print. */
  var _FOIL_STOPS =
    '<stop offset="0" stop-color="#b9d4ef"/>' +
    '<stop offset="0.15" stop-color="#a9e7f1"/>' +
    '<stop offset="0.31" stop-color="#cbc9f7"/>' +
    '<stop offset="0.47" stop-color="#f2c9e6"/>' +
    '<stop offset="0.63" stop-color="#f7dfc0"/>' +
    '<stop offset="0.79" stop-color="#c3edd8"/>' +
    '<stop offset="1" stop-color="#bcd0ee"/>';
  /* the specular streak that makes a flat gradient read as metal */
  var _SHEEN_STOPS =
    '<stop offset="0" stop-color="#ffffff" stop-opacity="0"/>' +
    '<stop offset="0.36" stop-color="#ffffff" stop-opacity="0"/>' +
    '<stop offset="0.5" stop-color="#ffffff" stop-opacity="0.5"/>' +
    '<stop offset="0.64" stop-color="#ffffff" stop-opacity="0"/>' +
    '<stop offset="1" stop-color="#ffffff" stop-opacity="0"/>';
  function foilify(s, SZ) {
    var defs = s.querySelector('defs');
    if (!defs) return;
    var gid = 'ltpv' + (++_n);
    var g = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
    g.setAttribute('id', gid);
    g.setAttribute('gradientUnits', 'userSpaceOnUse');
    g.setAttribute('x1', '0'); g.setAttribute('y1', '0');
    g.setAttribute('x2', String(SZ.longMm)); g.setAttribute('y2', String(SZ.shortMm));
    g.innerHTML = _FOIL_STOPS;
    defs.appendChild(g);
    var ref = 'url(#' + gid + ')';
    /* bake the foil ground INTO the svg — first paint inside the plate clip, so
       previews look right with zero external CSS */
    var gclip = s.querySelector('g[clip-path]');
    if (gclip) {
      var gnd = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      gnd.setAttribute('width', String(SZ.longMm));
      gnd.setAttribute('height', String(SZ.shortMm));
      gnd.setAttribute('fill', ref);
      gclip.insertBefore(gnd, gclip.firstChild);
      /* the streak goes on TOP of the sweep but UNDER everything the label
         prints — including a tint, which is ink laid over shining stock */
      var sh = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
      var sid = gid + 's';
      sh.setAttribute('id', sid);
      sh.setAttribute('gradientUnits', 'userSpaceOnUse');
      sh.setAttribute('x1', '0'); sh.setAttribute('y1', String(SZ.shortMm));
      sh.setAttribute('x2', String(SZ.longMm * 0.62));
      sh.setAttribute('y2', String(-SZ.shortMm * 0.35));
      sh.innerHTML = _SHEEN_STOPS;
      defs.appendChild(sh);
      var strk = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      strk.setAttribute('width', String(SZ.longMm));
      strk.setAttribute('height', String(SZ.shortMm));
      strk.setAttribute('fill', 'url(#' + sid + ')');
      gclip.insertBefore(strk, gnd.nextSibling);
    }
    /* ONLY '#ffffff' moves. That literal is this file's bare-foil declaration
       and nothing else in the tree — including registered background art — is
       touched, so an art fill previews as the ink it will print as. */
    var els = s.querySelectorAll('[fill="#ffffff"], [stroke="#ffffff"]');
    for (var i = 0; i < els.length; i++) {
      if (els[i].getAttribute('fill') === '#ffffff') els[i].setAttribute('fill', ref);
      if (els[i].getAttribute('stroke') === '#ffffff') els[i].setAttribute('stroke', ref);
    }
    /* the logo-backing gradient, addressed by THIS mount's namespace: art may
       define gradients of its own and must not be repainted by us */
    var ns = s.getAttribute('data-lt-ns');
    var f = ns ? s.querySelector('linearGradient[id="' + ns + 'f"]')
               : s.querySelector('linearGradient[id$="f"]');
    if (f) f.innerHTML = _FOIL_STOPS;
  }

  /* ── mount ────────────────────────────────────────────────────────────
     opts = { print: true|false, size_id: 'standard_177x075'|'large_250x100' }
     The die is EXPLICIT and never inferred. This file never reads window.st:
     a die silently inherited from a page's builder state is how a large label
     ends up rendered as a squashed standard cell.
     Returns { svg, minText, qrReal, dropped, sizeId }. Everything — the fit
     pass and the real QR modules — is finished before this returns, because
     the press sidecar serializes the node the instant the call comes back. */
  function mount(host, cfg, opts) {
    opts = opts || {};
    var SZ = sizeOf(pick(opts.size_id, opts.sizeId, opts.label_size_id,
      (cfg || {}).size_id, (cfg || {}).size));
    var html = svg(cfg, SZ.id), up = false;
    /* UPRIGHT — the builder's preview of a VERTICAL style (owner 2026-09-30:
       "orientation switching"). The die is cut landscape and every print and
       cart mount keeps it that way; the builder alone asks to see the label
       the way it will be applied. The whole die is wrapped in one rotation and
       the viewBox swapped, so the picture is the print cell turned 90 degrees
       and nothing inside it is drawn differently. */
    if (opts.upright) {
      var lk = (/data-lt-layout="([^"]+)"/.exec(html) || [])[1];
      var ll = lk ? layOf(lk, SZ.id) : null;
      if (ll && ll.key === lk && ll.orient === 'v') {
        up = true;
        html = html.replace('viewBox="0 0 ' + SZ.longMm + ' ' + SZ.shortMm + '"',
            'viewBox="0 0 ' + SZ.shortMm + ' ' + SZ.longMm + '" data-lt-upright="1"')
          .replace(/^(<svg[^>]*>)/, '$1<g transform="translate(' + SZ.shortMm + ' 0) rotate(90)">')
          .replace(/<\/svg>$/, '</g></svg>');
      }
    }
    host.innerHTML = html;
    if (host.classList) host.classList.toggle('lt-upright', up);
    var s = host.querySelector('svg');
    if (s) { s.style.width = '100%'; s.style.height = 'auto'; s.style.display = 'block'; }
    /* opts.white = Gloss White stock (2026-10-18): the print look IS the
       stock look - white ground, no foil sim */
    if (opts.print || opts.white) {
      host.classList.remove('lt-foil');
      host.style.background = '#ffffff';
    } else {
      ensureCss();
      host.classList.add('lt-foil');
      if (s) { try { foilify(s, SZ); } catch (_) {} }
    }
    var minText = s ? fit(s) : null;
    var real = s ? drawQr(s) : null;
    if (s) s.setAttribute('data-qr-real', real === null ? '' : (real ? '1' : '0'));
    var dropped = s && s.getAttribute('data-lt-dropped')
      ? s.getAttribute('data-lt-dropped').split(',') : [];
    return { svg: s, minText: minText, qrReal: real, dropped: dropped, sizeId: SZ.id };
  }

  window.LT = {
    SIZES: SIZES, sizeOf: sizeOf,
    LAYOUTS: LAYOUTS, layoutsFor: layoutsFor, BGS: BGS,
    pickerLayouts: pickerLayouts, startLayout: startLayout, START_BG: START_BG,
    pickerGrounds: pickerGrounds,
    DEFAULT_BG: DEFAULT_BG, registerBgArt: registerBgArt,
    norm: norm, svg: svg, mount: mount, fit: fit,
    safeReport: safeReport, overlapReport: overlapReport,
    qrPayload: qrPayload, qrCaption: qrCaption, qrFallback: qrFallback,
    qrBytes: qrBytes, QR_ACTIONS: QR_ACTIONS, QR_BUDGET: QR_BUDGET,
    esc: esc, inkSafe: inkSafe,
    DEFAULT_HDR: DEFAULT_HDR, DEFAULT_REWARD: DEFAULT_REWARD,
    /* the readability floor (mm) the builder blocks below, and the hard
       print-safe margin — one number each, one place */
    MIN_TEXT: MIN_TEXT, SAFE: SAFE
  };

  /* A background pack that was already on the page when this file parsed gets
     picked up for free, so a pack can either be loaded BEFORE lt-label.js or
     call LT.registerBgArt(LT_BG.list) after it. Nothing ships one today. */
  try {
    if (typeof window !== 'undefined' && window.LT_BG) registerBgArt(window.LT_BG);
  } catch (_) {}
})();

/* === LT BACKGROUND PACK (2026-09-05, foil-first tournament: scratchpad/lt_bgs/lt_bg_final.js)
   Shipped INSIDE lt-label.js on purpose: the Label Farm sidecar (tools/render_lost.py) pins
   only this file to the repo copy, so preview == cart == press for every ground. Keys are
   forever (a design record stores only bg:'<key>'). === */
/* lt_bg_final.js — LOST & FOUND CONTACT LABELS · the shipping ground set.
 * ---------------------------------------------------------------------------
 * Fifteen backgrounds selected from three candidate sets (travel / gear /
 * clean) by two judges, then re-cut here against the notes they left.
 *
 *   window.LT_BG = { author, list:[ {key,name,dark,tint,op,art(W,H,ns,dark,accent)} ] }
 *
 * HOLO FIRST. The stock is holographic vinyl printed CMYK-only. There is no
 * white ink: '#ffffff' is BARE FOIL, the brightest and most expensive-looking
 * thing this product makes. Thirteen of these fifteen lay no flood at all —
 * six are open stock with ink linework, six carry a translucent 0.14-0.26
 * screen, one is a full-plate material grain — and their type prints as INK.
 * None of the thirteen declares a `tint`: each paints its own screen as the
 * first shape of art(), so the wash percentage is the one the design chose and
 * not whatever the caller's tint branch happens to be set to. See INTEGRATION.md.
 * Only two flood (airmail, inkslab), both knock their type out to bare foil,
 * and both open genuine 100% foil windows rather than lightened tints.
 *
 * THE GALLERY ORDER IS THE PICKER ORDER
 *    1-6   open foil     nothing but stock and ink drawing
 *    7-12  tinted foil   one translucent screen, the shift still working
 *    13    textured      a full-plate material
 *    14-15 dark          the two ink plates, last
 *
 * ---------------------------------------------------------------------------
 * PRESS RULES, ENFORCED IN CODE, NOT IN COMMENTS
 *   · S() floors every stroke at 0.13 mm and every ink stroke opacity at 0.20.
 *     Both judges killed otherwise-good grounds for 0.10-0.14 mm linework run
 *     at 0.05-0.17 opacity: on a C6000 that either dithers out or prints
 *     ratty, so the preview flatters a plate the press cannot lay. Nothing in
 *     this file can be edited back under those floors by accident.
 *   · F() floors fill opacity at 0.16 (area survives better than a hairline).
 *   · Every ink hex here has HSL lightness <= 0.45, so LT.inkSafe() returns it
 *     unchanged — what is written is what the press lays. Pale tones are made
 *     ONLY by screening a dark ink; a pale hex would be clamped to L 0.40.
 *   · The customer accent arrives already clamped by norm(); acc() clamps it
 *     again so an art() called directly is still safe.
 *   · Deterministic: four seeded LCGs (seachart soundings, circuit drops,
 *     hairline grain, anodize grain). Nothing in this file is random.
 *   · No rasters, no external references, no type, no <pattern>, no <mask>.
 *     clipPath only, which lt-label.js itself uses, so the press path has it.
 *   · Every id is ns + a suffix, and no id ends in "f" — foilify() rewrites
 *     the linearGradient whose id ends in "f" for this mount.
 *   · Node budget 400. Measured: 1 node (textpanel) to 16 (airmail);
 *     the whole label lands at 361-379 nodes on both dies.
 *
 * THE TWO DARK GROUNDS declare BOTH a `tint` and an opaque flood of the same
 * ink as the first shape of art(). That is deliberate, and it is the one thing
 * an integrator must not "tidy up":
 *   · registerBgArt() clamps a declared tint's screen to 0.60 max, and the
 *     contact-sheet harness paints it at 0.86 — so a tint alone gives a
 *     different plate in the two places and neither is dark enough for
 *     knockout type. The art's own flood makes both identical and opaque.
 *   · Declaring the tint anyway keeps grounded() true, keeps the harness from
 *     warning, and leaves a sane dark plate behind if art() ever throws.
 *
 * ---------------------------------------------------------------------------
 * THE ZONES. Measured off drawCallout (std) and drawCalloutLg (lg), as
 * fractions, because one drawing has to compose for both dies:
 *
 *                              standard 44.958x19.05   large 63.5x25.4
 *   text column          x     0.045 - 0.681 W         0.041 - 0.696 W
 *   code column / QR     x     0.710 - 0.954 W         0.718 - 0.970 W
 *   QR patch             y     0.136 - 0.714 H         0.185 - 0.815 H
 *   header cap top       y     0.134 H                 0.138 H
 *   renderer accent rule y     0.231 H                 0.244 H
 *   name band            y     0.247 - 0.399 H         0.260 - 0.409 H
 *   hero band            y     0.436 - 0.609 H         0.449 - 0.630 H
 *   detail rows          y     0.667 - 0.929 H         0.675 - 0.933 H
 *   reward bar           y     0.808 - 0.929 H         0.886 - 0.961 H
 *   printed fallback     y     0.753 - 0.808 H         0.813 - 0.866 H
 *
 *   SEAM  = 0.703 W   the only vertical clear of the text on BOTH dies
 *   G2    = 0.4225 H  the name/hero gutter, empty on both
 *   G3    = 0.648 H   the hero/details gutter, empty on both
 *   POCKET  x > 0.708 W, y 0.882 - 0.940 H — clear on both dies, the one place
 *           in the code column a design may be loud under a fitted QR.
 *   KEYLINE the renderer draws a 0.18 mm keyline 1.0 mm in from the cut, so it
 *           occupies 0.91-1.09 mm. Furniture that bleeds to the cut is sized in
 *           ABSOLUTE mm, never as a fraction of the die, and it either stops
 *           before 0.91 or covers past 1.09 — a fraction lands on one side of
 *           that on the standard die and the other side on the large, which is
 *           one design behaving as two.
 *
 *   NOTE ON G1: the header/name gutter is NOT used by anything here. The
 *   renderer already draws its own accent rule at 0.231/0.244 H and a second
 *   rule 0.2 mm away reads as a registration fault, not a design.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  /* ── the material and the inks ──────────────────────────────────────── */
  var FOIL = '#ffffff';        /* BARE FOIL. never white ink. */
  var INK  = '#04050a';
  var NAVY = '#10233f';        /* contour           L 0.155 */
  var SLATE= '#24425e';        /* meridian wash     L 0.255 */
  var TEAL = '#0a3a44';        /* sea chart wash    L 0.153 */
  var DEEP = '#06363c';        /* sea chart line    L 0.129 */
  var FOREST='#0d3324';        /* ridgeline         L 0.125 */
  var CHAR = '#16181d';        /* compass, air mail L 0.087 */
  var BLUE = '#0b3a7a';        /* duotone screen    L 0.261 */
  var PLUM = '#3a1163';        /* text panel screen L 0.227 */
  var BOARD= '#16242e';        /* circuit wash      L 0.133 */
  var SLAB = '#0b1730';        /* ink slab plate    L 0.117 */

  /* ── cross-die anchors (see the zone table above) ───────────────────── */
  var SEAM = 0.703, G2 = 0.4225, G3 = 0.648;
  /* mm, absolute on both dies. A foil rail that bleeds to the cut has to be
     WIDER than the renderer's keyline (1.0 mm in, 0.18 mm thick, so 0.91-1.09)
     or it half-swallows it — and a fraction-of-die rail swallows it on one size
     and not the other. 1.30 mm clears the keyline's inner edge by 0.21 mm and
     the standard die's QR caption cap by 0.20 mm. */
  var RAIL = 1.30;

  /* ── weights. three widths, and a floor nothing can slip under ─────── */
  var HAIR = 0.13, RULE = 0.16, BOLD = 0.20;

  function r2(n) { return Math.round(n * 100) / 100; }
  function r3(n) { return Math.round(n * 1000) / 1000; }
  function lcg(seed) {
    var s = (seed >>> 0) || 1;
    return function () { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
  }
  /* the customer accent, clamped exactly as the auditor clamps it */
  function acc(c) {
    try {
      if (window.LT && window.LT.inkSafe) return String(window.LT.inkSafe(c)).toLowerCase();
    } catch (_) {}
    return /^#[0-9a-fA-F]{6}$/.test(String(c || '')) ? String(c).toLowerCase() : '#2c1f6b';
  }

  /* ── path atoms ─────────────────────────────────────────────────────── */
  function M(x, y) { return 'M' + r2(x) + ' ' + r2(y); }
  function L(x, y) { return 'L' + r2(x) + ' ' + r2(y); }
  function seg(x1, y1, x2, y2) { return M(x1, y1) + L(x2, y2); }
  function circ(cx, cy, r) {
    return M(cx - r, cy) + 'a' + r2(r) + ' ' + r2(r) + ' 0 1 0 ' + r2(2 * r) + ' 0' +
           'a' + r2(r) + ' ' + r2(r) + ' 0 1 0 ' + r2(-2 * r) + ' 0';
  }
  function ell(cx, cy, rx, ry) {
    return M(cx - rx, cy) + 'a' + r2(rx) + ' ' + r2(ry) + ' 0 1 0 ' + r2(2 * rx) + ' 0' +
           'a' + r2(rx) + ' ' + r2(ry) + ' 0 1 0 ' + r2(-2 * rx) + ' 0';
  }
  function box(x, y, w, h) { return M(x, y) + 'h' + r2(w) + 'v' + r2(h) + 'h' + r2(-w) + 'Z'; }

  /* THE TWO FLOORS. A stroke is at least 0.13 mm; ink is at least 0.20
     opacity. Bare foil is exempt from the opacity floor only above 0.22,
     because a foil mark on a dark plate at less than that is not a window,
     it is a slightly lighter screen of the plate — which is what got two
     otherwise handsome dark grounds cut in review. */
  function S(d, col, w, o, extra) {
    var lo = (col === FOIL) ? 0.22 : 0.20;
    return '<path d="' + d + '" fill="none" stroke="' + col + '" stroke-width="' +
      r3(Math.max(0.13, w)) + '" opacity="' + r3(Math.max(lo, Math.min(1, o))) + '"' +
      (extra || '') + '/>';
  }
  function F(d, col, o) {
    return '<path d="' + d + '" fill="' + col + '" opacity="' +
      r3(Math.max(0.16, Math.min(1, o))) + '"/>';
  }
  /* a rect, in the label's own mm space. `o` is the ink screen percentage —
     which is literally how a tint prints on this stock. */
  function R(x, y, w, h, col, o) {
    return '<rect x="' + r2(x) + '" y="' + r2(y) + '" width="' + r2(w) + '" height="' +
      r2(h) + '" fill="' + col + '"' +
      (o == null ? '' : ' fill-opacity="' + r3(o) + '"') + '/>';
  }
  function wash(W, H, col, o) { return R(0, 0, W, H, col, o); }
  function clipDef(ns, id, x, y, w, h) {
    return '<clipPath id="' + ns + id + '"><rect x="' + r2(x) + '" y="' + r2(y) +
      '" width="' + r2(w) + '" height="' + r2(h) + '"/></clipPath>';
  }
  function clipped(ns, id, inner) {
    return '<g clip-path="url(#' + ns + id + ')">' + inner + '</g>';
  }
  function rot(a, cx, cy, inner) {
    return '<g transform="rotate(' + r2(a) + ' ' + r2(cx) + ' ' + r2(cy) + ')">' + inner + '</g>';
  }
  /* mean of the two die scales. TEXTURE PITCH rides this; line WEIGHT never
     does — weight is a material constant in mm on both sizes. */
  function sc(W, H) { return (W / 44.958 + H / 19.05) / 2; }
  function reach(W, H) { return Math.sqrt(W * W + H * H) / 2 + 1.5; }

  /* a sine run built from half-period quadratics */
  function waveD(x0, x1, y, amp, wl, up) {
    var hw = wl / 2, x = x0, d = M(x0, y), n = 0;
    while (x < x1 && n++ < 120) {
      var nx = Math.min(x + hw, x1);
      d += 'Q' + r2((x + nx) / 2) + ' ' + r2(y + (up ? -amp : amp) * 1.35) + ' ' + r2(nx) + ' ' + r2(y);
      x = nx; up = !up;
    }
    return d;
  }
  /* a closed contour: a circle with two harmonics on the radius */
  function ring(cx, cy, rad, sqz, wob, ph, n) {
    var d = '', i, a, rr;
    for (i = 0; i < n; i++) {
      a = i / n * Math.PI * 2;
      rr = rad * (1 + wob * Math.sin(3 * a + ph) + wob * 0.5 * Math.sin(5 * a - ph * 1.7));
      d += (i ? L : M)(cx + rr * Math.cos(a), cy + rr * sqz * Math.sin(a));
    }
    return d + 'Z';
  }
  /* one point of a compass rose */
  function point(cx, cy, a, len, w) {
    var p = a + Math.PI / 2;
    return M(cx + Math.cos(a) * len, cy + Math.sin(a) * len) +
           L(cx + Math.cos(p) * w, cy + Math.sin(p) * w) + L(cx, cy) +
           L(cx - Math.cos(p) * w, cy - Math.sin(p) * w) + 'Z';
  }
  /* a bowed great-circle run */
  function arcQ(x0, y0, x1, y1, bow) {
    var mx = (x0 + x1) / 2, my = (y0 + y1) / 2, dx = x1 - x0, dy = y1 - y0;
    var len = Math.sqrt(dx * dx + dy * dy) || 1;
    return M(x0, y0) + 'Q' + r2(mx + (-dy / len) * bow) + ' ' + r2(my + (dx / len) * bow) +
           ' ' + r2(x1) + ' ' + r2(y1);
  }
  /* a skyline closed down past the cut */
  function skyline(W, H, pts) {
    var d = M(-W * 0.04, H * 1.06), i;
    for (i = 0; i < pts.length; i++) d += L(pts[i][0] * W, pts[i][1] * H);
    return d + L(W * 1.04, H * 1.06) + 'Z';
  }
  /* an air-mail chevron run inside an explicit box, phase 0 or 1 */
  function chevrons(x0, x1, y0, y1, wdt, phase) {
    var sl = (y1 - y0) * 0.58, d = '', x = x0 - sl - wdt * 2, i = 0;
    while (x < x1 + wdt * 2 && i++ < 160) {
      if ((i & 1) === phase) {
        d += M(x, y1) + L(x + sl, y0) + L(x + sl + wdt, y0) + L(x + wdt, y1) + 'Z';
      }
      x += wdt;
    }
    return d;
  }
  /* an unbroken ruled family on `ang` — a continuous crosshatch, never dashed */
  function ruled(cx, cy, Rr, ang, pitch, sw, col, op) {
    var d = '', y;
    for (y = cy - Rr; y <= cy + Rr; y += pitch) d += M(cx - Rr, y) + 'H' + r2(cx + Rr);
    return rot(ang, cx, cy, S(d, col, sw, op));
  }

  window.LT_BG = {
    author: 'final',
    list: [

/* ══ 1 · TWIN RULES ═════════════════════════════════ open foil ══════════
   The quietest thing in the set and the one both judges scored a straight
   ten: nothing but stock, two rules set in measured type gutters, and one
   short accent segment riding the upper rule.
   CUT SINCE REVIEW: the rules were at G1/G2. G1 is 0.2 mm from the renderer's
   own accent rule on the standard die, so the pair printed as one fat wobbly
   line. Moved to G2/G3, where they now bracket the phone number instead —
   a better composition and a gutter that is genuinely empty on both dies.
   The accent stub was lengthened to 0.185 W so it reads as a mark. */
    { key: 'tworule', name: 'Twin Rules', dark: false,
      art: function (W, H, ns, dark, accent) {
        var c = dark ? FOIL : INK, r = W * SEAM, y2 = H * G2, y3 = H * G3;
        return S(seg(0, y2, r, y2), c, BOLD, 0.55) +
               S(seg(0, y3, r, y3), c, BOLD, 0.45) +
               S(seg(0, y2, W * 0.185, y2), acc(accent), 0.42, 0.9);
      } },

/* ══ 2 · CONTOUR ════════════════════════════════════ open foil ══════════
   A survey sheet on bare stock — the best expression of the brief in the
   whole field, and the only ground both judges ranked first. One hill is
   parked in the code column where a fitted QR masks its dense middle; a
   second landform walks in off the bottom-left corner. Index contours every
   third ring carry the extra weight, as a real map does.
   CUT SINCE REVIEW: the outer bands ran at 0.20/0.26 — the one marginal ink
   in an otherwise clean design. Lifted to 0.24/0.30 and the base weight from
   0.11 to 0.13 mm, so every arc is now above what the press reliably lays. */
    { key: 'contour', name: 'Contour', dark: false,
      art: function (W, H, ns, dark, accent) {
        var c = dark ? FOIL : NAVY, o = [], i, d;
        var ax = W * 0.775, ay = H * 0.34;
        var band = ['', '', ''], idx = ['', '', ''];
        for (i = 0; i < 9; i++) {
          d = ring(ax, ay, H * (0.09 + i * 0.086), 0.88, 0.12, 0.6 + i * 0.21, 38);
          var b = i < 3 ? 0 : (i < 6 ? 1 : 2);
          if (i % 3 === 2) idx[b] += d; else band[b] += d;
        }
        var op = [0.50, 0.34, 0.24], iop = [0.66, 0.44, 0.30];
        for (i = 0; i < 3; i++) {
          if (band[i]) o.push(S(band[i], c, HAIR, op[i]));
          if (idx[i]) o.push(S(idx[i], c, BOLD, iop[i]));
        }
        var lo = '', li = '';
        for (i = 0; i < 6; i++) {
          d = ring(W * 0.10, H * 1.30, H * (0.30 + i * 0.135), 0.92, 0.09, 2.3 - i * 0.29, 42);
          if (i % 3 === 1) li += d; else lo += d;
        }
        o.push(S(lo, c, HAIR, 0.26));
        o.push(S(li, c, RULE, 0.34));
        o.push(F(circ(ax + H * 0.04, ay - H * 0.03, 0.19) +
                 circ(W * 0.19, H * 0.72, 0.17), c, 0.58));
        return o.join('');
      } },

/* ══ 3 · COMPASS ROSE ═══════════════════════════════ open foil ══════════
   A portolan chart. The rose sits in the code column where the layout is
   quiet on both dies; the sixteen rhumb lines run edge to edge and are the
   part of the drawing a fitted QR can never hide.
   CUT SINCE REVIEW: the rhumbs were 0.10 mm at 0.14 opacity — under the
   press floor twice over, and the one reason this ground was split. Now
   0.14 mm at 0.20, which is exactly the runner-up weight the reviewer named,
   and it turns the rays from a whisper into the design. The rose grew from
   0.44 to 0.45 H and moved 0.013 W left so more of its outer ring stands
   clear above and below a fitted patch. */
    { key: 'compass', name: 'Compass Rose', dark: false,
      art: function (W, H, ns, dark, accent) {
        var c = dark ? FOIL : CHAR, o = [], i, a, t, ri;
        var cx = W * 0.752, cy = H * 0.50, Rr = H * 0.45, TAU = Math.PI * 2;
        var rh = '', span = W * 1.5;
        for (i = 0; i < 16; i++) {
          a = i / 16 * TAU;
          rh += seg(cx, cy, cx + Math.cos(a) * span, cy + Math.sin(a) * span);
        }
        o.push(S(rh, c, 0.14, 0.20));
        var tk = '';
        for (i = 0; i < 32; i++) {
          t = i / 32 * TAU; ri = Rr * (i % 4 ? 0.90 : 0.83);
          tk += seg(cx + Math.cos(t) * ri, cy + Math.sin(t) * ri,
                    cx + Math.cos(t) * Rr, cy + Math.sin(t) * Rr);
        }
        o.push(S(tk, c, HAIR, 0.44));
        o.push(S(circ(cx, cy, Rr), c, RULE, 0.50));
        o.push(S(circ(cx, cy, Rr * 0.80), c, HAIR, 0.30));
        var lon = '', sho = '';
        for (i = 0; i < 4; i++) {
          lon += point(cx, cy, i * Math.PI / 2 - Math.PI / 2, Rr * 0.76, Rr * 0.13);
          sho += point(cx, cy, i * Math.PI / 2 - Math.PI / 4, Rr * 0.48, Rr * 0.09);
        }
        o.push(S(sho, c, HAIR, 0.32));
        o.push(S(lon, c, RULE, 0.50));
        o.push(F(circ(cx, cy, Rr * 0.15), c, 0.40));
        o.push(F(point(cx, cy, -Math.PI / 2, Rr * 0.76, Rr * 0.13), c, 0.62));
        /* The needle is SHORT — 0.55 R, nested inside the ink south point.
           At the full 0.76 R its tip landed on the printed phone fallback at
           0.842 H on the standard die; at 0.55 R it stops at 0.748 H, above
           the fallback cap on std and inside the patch on large. The accent
           is now the only saturated mark on the plate and it touches nothing. */
        o.push(F(point(cx, cy, Math.PI / 2, Rr * 0.55, Rr * 0.13), acc(accent), 0.80));
        return o.join('');
      } },

/* ══ 4 · DOT GRID ═══════════════════════════════════ open foil ══════════
   The safest construction of the thirty reviewed: a near-zero dash under a
   round cap is a dot, so a whole row of dots is one subpath and the entire
   field is three paths of 0.28 mm dots — comfortably above the vanishing
   point. A 1.2 mm tick scale along the top strip gives it the instrument
   voice and sits where both dies are empty.
   CUT SINCE REVIEW: the mid-field band ran at 0.17; lifted to the 0.20 floor. */
    { key: 'dotgrid', name: 'Dot Grid', dark: false,
      art: function (W, H, ns, dark, accent) {
        var c = dark ? FOIL : INK, s = sc(W, H), pitch = 1.06 * s;
        var band = ['', '', ''], y, i;
        for (y = pitch * 0.5; y < H; y += pitch) {
          var f = y / H;
          band[f < 0.15 ? 0 : (f > 0.80 ? 2 : 1)] += M(-pitch, y) + 'H' + r2(W + pitch);
        }
        var op = [0.30, 0.20, 0.26], o = [];
        for (i = 0; i < 3; i++) {
          if (!band[i]) continue;
          o.push(S(band[i], c, 0.28, op[i],
            ' stroke-linecap="round" stroke-dasharray="0.02 ' + r3(pitch) + '"'));
        }
        var t1 = '', t5 = '', k = 0, x;
        for (x = W * 0.04; x < W * 0.97; x += 1.2 * s, k++) {
          if (k % 5 === 0) t5 += M(x, H * 0.028) + 'V' + r2(H * 0.105);
          else t1 += M(x, H * 0.028) + 'V' + r2(H * 0.072);
        }
        o.push(S(t1, c, 0.14, 0.28));
        o.push(S(t5, c, 0.18, 0.40));
        return o.join('');
      } },

/* ══ 5 · APERTURE ═══════════════════════════════════ open foil ══════════
   Six nodes, no repeating cell, nothing tiled: a nine-blade iris parked on the
   code column, where it reads as a machined bezel around a fitted QR and as a
   whole lens on the layouts that carry no code.
   CUT SINCE REVIEW — TWICE. The reviewed version put a 0.30 H opening dead
   under the patch, so with a code fitted the customer bought two faint barrel
   arcs. Opening it to 0.42 H fixed that and broke something worse: at that
   radius the nine blade edges become long shallow diagonals running out past
   the label edges, and at 1:1 they read as cracks in the plate, not as a lens.
   The blades are now SHORT — each one spans 0.34 H to 0.50 H only — so they
   close into a pinwheel between two concentric rings instead of escaping
   across the type. The opening still clears the patch left and below on both
   dies, and one outer bezel ring the die crops gives the barrel its depth. */
    { key: 'aperture', name: 'Aperture', dark: false,
      art: function (W, H, ns, dark, accent) {
        var c = dark ? FOIL : INK, cx = W * 0.775, cy = H * 0.5, i;
        var TAU = Math.PI * 2, N = 9, turn = -0.25;
        var rIn = H * 0.34, rMid = H * 0.50, rBez = H * 0.62;
        function at(Rr, t) {
          return r2(cx + Rr * Math.cos(t * TAU)) + ' ' + r2(cy + Rr * Math.sin(t * TAU));
        }
        var iris = '', blade = '', collar = '';
        for (i = 0; i <= N; i++) {
          iris += (i ? 'L' : 'M') + at(rIn, turn + i / N);
          collar += (i ? 'L' : 'M') + at(rMid, turn + i / N + 0.105);
        }
        for (i = 0; i < N; i++) {
          blade += 'M' + at(rIn, turn + i / N) + 'L' + at(rMid, turn + i / N + 0.105);
        }
        return S(blade, c, RULE, 0.24) +
               S(collar, c, HAIR, 0.22) +
               S(iris + 'Z', c, 0.22, 0.34) +
               S(circ(cx, cy, rBez), c, BOLD, 0.26) +
               S(circ(cx, cy, rBez * 1.22), c, RULE, 0.20);
      } },

/* ══ 6 · RIDGELINE ══════════════════════════════════ open foil ══════════
   The sky — the whole upper half, where the name goes — is left completely
   empty stock, and the wash is the land: two ranges laid in as flat values
   with the snow hatch only ever on a peak. Best-composed ground of the field.
   CUT SINCE REVIEW: on the large die the address rows crossed the mid-value
   fill. The fills are now 0.17 and 0.26 flat — down from 0.20 and 0.36 — and
   the whole landform dropped 0.04 H to widen the sky, so the detail rows sit
   on the lightest ink on the plate and the ridge outlines carry the drawing.
   (An opacity RAMP was tried first — light across the text column, topped up
   inside a clip from 0.42 W rightward. It fixed the legibility and printed a
   hard vertical step down the middle of the mountains, which looked like a
   registration fault. A flat value with stronger outlines is the honest fix.) */
    { key: 'ridgeline', name: 'Ridgeline', dark: false,
      art: function (W, H, ns, dark, accent) {
        var c = dark ? FOIL : FOREST, o = [], i;
        var back = [[0.02, 0.78], [0.16, 0.54], [0.27, 0.67], [0.41, 0.48],
                    [0.55, 0.66], [0.68, 0.51], [0.80, 0.34], [0.92, 0.56], [1.0, 0.48]];
        var front = [[0.0, 0.93], [0.11, 0.76], [0.24, 0.87], [0.38, 0.70],
                     [0.50, 0.83], [0.63, 0.74], [0.76, 0.89], [0.88, 0.68], [1.0, 0.81]];
        o.push(F(skyline(W, H, back), c, 0.17));
        o.push(F(skyline(W, H, front), c, 0.26));
        var bl = '', fl = '';
        for (i = 0; i < back.length - 1; i++) {
          bl += seg(back[i][0] * W, back[i][1] * H, back[i + 1][0] * W, back[i + 1][1] * H);
        }
        for (i = 0; i < front.length - 1; i++) {
          fl += seg(front[i][0] * W, front[i][1] * H, front[i + 1][0] * W, front[i + 1][1] * H);
        }
        o.push(S(bl, c, HAIR, 0.42));
        o.push(S(fl, c, RULE, 0.66));
        var sn = '', pk = [[0.80, 0.34], [0.41, 0.48], [0.88, 0.68]];
        for (i = 0; i < pk.length; i++) {
          var px = pk[i][0] * W, py = pk[i][1] * H;
          sn += seg(px - W * 0.020, py + H * 0.075, px - W * 0.006, py + H * 0.028) +
                seg(px + W * 0.007, py + H * 0.032, px + W * 0.022, py + H * 0.082);
        }
        o.push(S(sn, c, HAIR, 0.48));
        return o.join('');
      } },

/* ══ 7 · DUOTONE ════════════════════════════════════ tinted foil ═══════
   The horizontal two-tone: header and name keep open stock, the hero line
   down sits in a navy screen whose edge lands in the measured name/hero
   gutter on BOTH dies, so nothing is bisected.
   HELD AT 20% AFTER REVIEW. One reviewer wanted it lighter (the renderer's
   bare-foil halo pops against the screen and the phone number reads slightly
   glowy); the other named 20% as the floor at which a large FLAT tint stops
   showing dither and nozzle streak on gloss. 17% was tried and it is the wrong
   trade: the halo is a preview artefact of an ink-on-foil outline, the dither
   is a real press defect. Press safety wins. (The renderer softens that halo
   only for grounds that declare a `tint` — see INTEGRATION.md for the one-line
   change that would let an art-painted wash ask for the same treatment.) */
    { key: 'duotone', name: 'Duotone', dark: false,
      art: function (W, H, ns, dark, accent) {
        var y = H * G2;
        return R(0, y, W, H - y, BLUE, 0.20) +
               S(seg(0, y, W * SEAM, y), INK, BOLD, 0.5);
      } },

/* ══ 8 · TEXT PANEL ═════════════════════════════════ tinted foil ═══════
   One rect, and the smartest one reviewed: the type sits on a calm screen
   and the CODE COLUMN IS LEFT AS OPEN STOCK, so a QR reads out of the
   brightest field on the plate.
   CUT SINCE REVIEW: 15% is in the zone where a flat tint shows dither and
   nozzle streak on gloss. Raised to 20% — the same floor Duotone holds, so the
   set has ONE number for a large flat screen rather than two near guesses. */
    { key: 'textpanel', name: 'Text Panel', dark: false,
      art: function (W, H, ns, dark, accent) {
        return R(0, 0, W * SEAM, H, PLUM, 0.20);
      } },

/* ══ 9 · SEA CHART ══════════════════════════════════ tinted foil ═══════
   A teal screen at 26% and nothing but water. Horizontal swell is the most
   type-friendly texture in the whole field — it never brackets a word — one
   heavier depth contour carries the drawing with hatching on its shoal side,
   and the soundings are a seeded scatter of 0.34 mm dots.
   Scored 9/9/9 by both reviewers; the only change is the base weight moving
   from 0.11 to the 0.13 mm house floor. */
    { key: 'seachart', name: 'Sea Chart', dark: false,
      art: function (W, H, ns, dark, accent) {
        var c = dark ? FOIL : DEEP, o = [wash(W, H, TEAL, 0.26)], i, x;
        var sw = '';
        for (i = 0; i < 6; i++) {
          sw += waveD(-W * 0.03, W * 1.03, H * (0.10 + i * 0.163), H * 0.021, W * 0.30, i % 2 === 0);
        }
        o.push(S(sw, c, HAIR, 0.32));
        var cy = H * 0.80, amp = H * 0.030, wl = W * 0.26;
        o.push(S(waveD(W * 0.30, W * 1.03, cy, amp, wl, true), c, BOLD, 0.58));
        var ht = '';
        for (i = 0, x = W * 0.33; x < W; i++, x += W * 0.035) {
          var hy = cy - Math.sin((x - W * 0.30) / (wl / 2) * Math.PI) * amp * 0.675;
          ht += seg(x, hy, x, hy + H * 0.055);
        }
        o.push(S(ht, c, HAIR, 0.42));
        var rnd = lcg(20260905), so = '';
        for (i = 0; i < 26; i++) {
          so += circ(W * (0.03 + rnd() * 0.95), H * (0.06 + rnd() * 0.90), 0.17);
        }
        o.push(F(so, c, 0.38));
        return o.join('');
      } },

/* ══ 10 · MERIDIAN ══════════════════════════════════ tinted foil ═══════
   A slate screen at 25% — light enough that the rainbow survives it — with a
   globe bleeding off the code column and a far smaller one clipped by the
   bottom-left corner, so the plate reads as two places rather than a diagram.
   CUT SINCE REVIEW: the far globe's meridians converged under the email row,
   the smallest ink on the label. It has been pushed down (centre 1.14 H ->
   1.26 H) and its graticule cut to a single arc at the floor opacity, and the
   main graticule dropped from 0.42/0.44 to 0.34/0.36 so only the limb and the
   equator carry weight where the type is. */
    { key: 'meridian', name: 'Meridian', dark: false,
      art: function (W, H, ns, dark, accent) {
        var c = dark ? FOIL : NAVY, o = [wash(W, H, SLATE, 0.25)], i, yy;
        var cx = W * 0.795, cy = H * 0.50, Rr = H * 0.60;
        var lat = [-0.62, -0.33, 0.33, 0.62], mer = [0.30, 0.60, 0.86];
        o.push(S(circ(cx, cy, Rr), c, BOLD, 0.62));
        var g = '';
        for (i = 0; i < mer.length; i++) g += ell(cx, cy, Rr * mer[i], Rr);
        g += seg(cx, cy - Rr, cx, cy + Rr);
        o.push(S(g, c, HAIR, 0.34));
        var chord = '';
        for (i = 0; i < lat.length; i++) {
          yy = cy + Rr * lat[i];
          var hw = Rr * Math.sqrt(1 - lat[i] * lat[i]);
          chord += seg(cx - hw, yy, cx + hw, yy);
        }
        o.push(S(chord, c, HAIR, 0.36));
        o.push(S(seg(cx - Rr, cy, cx + Rr, cy), c, RULE, 0.66));
        var sx = W * 0.055, sy = H * 1.26, sr = H * 0.46;
        o.push(S(circ(sx, sy, sr), c, RULE, 0.30));
        o.push(S(seg(sx - sr, sy, sx + sr, sy), c, HAIR, 0.20));
        o.push(F(circ(cx, cy - Rr, 0.21) + circ(cx, cy + Rr, 0.21), c, 0.62));
        return o.join('');
      } },

/* ══ 11 · TRACE ══ ═══════════════════════════════════ tinted foil ═══════
   The KEY stays 'circuit' — keys are forever and every design already sold
   on this ground carries it. The display NAME moved because the artwork
   catalogue owns 'Circuit' in the same picker, and a customer who cannot
   say which of two identically named grounds they bought is a customer
   support cannot help on a reorder.
   A 20% board screen so the foil stays alive, busses and pads at full weight
   inside the two quiet strips, and every rail kept INSIDE the 1.0 mm margin
   because a rail laid in the bleed is a rail the die may take away.
   CUT SINCE REVIEW: the drops that cross the text corridor were 0.14 mm at
   0.10 opacity and the pads 0.16 — the board was losing precisely the routing
   that makes it a board. Drops now 0.15 mm at 0.20 and pads at 0.24, the
   weight the reviewer asked for and the lightest this press will actually lay. */
    { key: 'circuit', name: 'Trace', dark: false,
      art: function (W, H, ns, dark, accent) {
        var c = dark ? FOIL : INK, s = sc(W, H), rnd = lcg(77012), o = [], i;
        if (!dark) o.push(wash(W, H, BOARD, 0.20));
        var topY = [H * 0.058, H * 0.092, H * 0.126], botY = [H * 0.968, H * 0.992];
        var rail = '';
        for (i = 0; i < topY.length; i++) rail += M(0, topY[i]) + 'H' + r2(W);
        for (i = 0; i < botY.length; i++) rail += M(0, botY[i]) + 'H' + r2(W);
        o.push(S(rail, c, RULE, 0.30));
        var drop = '', pads = '', el = 0.9 * s;
        for (i = 0; i < 9; i++) {
          var x0 = W * (0.055 + 0.105 * i) + 0.4 * s * rnd();
          var down = rnd() > 0.45;
          var y0 = down ? topY[2] : botY[0];
          var y1 = H * (0.30 + 0.42 * rnd());
          var dir = rnd() > 0.5 ? 1 : -1;
          var sgn = down ? 1 : -1;
          drop += M(x0, y0) + 'V' + r2(y1 - sgn * el) +
                  L(x0 + dir * el, y1) + 'H' + r2(x0 + dir * (el + 1.6 * s));
          pads += circ(x0 + dir * (el + 1.6 * s), y1, 0.26 * s);
        }
        o.push(S(drop, c, 0.15, 0.20));
        o.push(F(pads, c, 0.24));
        var bp = '';
        for (i = 0; i < 11; i++) bp += circ(W * (0.045 + 0.091 * i), topY[1], 0.30 * s);
        o.push(F(bp, c, 0.34));
        o.push(S(seg(W * 0.06, topY[0], W * 0.62, topY[0]), acc(accent), 0.22, 0.55));
        return o.join('');
      } },

/* ══ 12 · ANODIZED ══════════════════════════════════ tinted foil ═══════
   The one ground the buyer colours: a translucent screen of the customer's
   own clamped accent over bare foil, with vertical extrusion grain that also
   masks any dither banding, and three specular columns where the grain stops.
   CUT SINCE REVIEW: the accent screen was 22% across the whole plate, which
   made the label a colour rather than a foil with colour on it — a warm
   accent looked wrong. Down to 14%. The grain was 0.045-0.18 opacity and
   would simply not have laid down: it now SKIPS a line entirely below the
   threshold (a specular is the absence of ink, the only highlight this stock
   can make) and prints the rest at 0.20-0.34. */
    { key: 'anodize', name: 'Anodized', dark: false,
      art: function (W, H, ns, dark, accent) {
        var c = dark ? FOIL : INK, s = sc(W, H), a = acc(accent);
        var pitch = 0.30 * s, rnd = lcg(31337), B = 4, i, x;
        var band = []; for (i = 0; i < B; i++) band[i] = '';
        for (x = pitch * 0.5; x < W; x += pitch) {
          var f = x / W;
          var spec = 1 -
            0.72 * Math.exp(-Math.pow((f - 0.17) / 0.055, 2)) -
            0.62 * Math.exp(-Math.pow((f - 0.52) / 0.05, 2)) -
            0.66 * Math.exp(-Math.pow((f - 0.86) / 0.045, 2));
          var v = (0.34 + 0.66 * rnd()) * Math.max(0, spec);
          /* 0.62, not the 0.55 Brushed Alloy uses: this grain is VERTICAL and
             crosses every line of type, so it has to be sparser to read as
             quietly as a horizontal grain of the same weight does. */
          if (v < 0.62) continue;                       /* bare foil — a real specular */
          var b = Math.min(B - 1, Math.max(0, Math.floor((v - 0.62) / 0.38 * B)));
          band[b] += M(x, 0) + 'V' + r2(H);
        }
        var o = [wash(W, H, a, 0.14)];
        for (i = 0; i < B; i++) if (band[i]) o.push(S(band[i], c, HAIR, 0.20 + i * 0.047));
        o.push(S(seg(0, H * 0.068, W, H * 0.068), c, 0.18, 0.26));
        o.push(S(seg(0, H * 0.975, W, H * 0.975), c, 0.18, 0.22));
        return o.join('');
      } },

/* ══ 13 · BRUSHED ALLOY ═════════════════════════════ textured foil ═════
   The only full-plate material that survived review: the stock IS the metal,
   the grain is ink, and the highlight is the absence of ink. The text
   corridor stays legible because the grain is horizontal and even.
   CUT SINCE REVIEW: both reviewers said the lightest bands (0.11 mm at
   0.05-0.14) would not lay down, so the print would read flatter than the
   preview. Same fix as Anodized — the specular zones now skip their lines
   outright and everything drawn is 0.13 mm at 0.20-0.34, plus deeper drag
   marks. What you see on the sheet is now what the press puts down. */
    { key: 'hairline', name: 'Brushed Alloy', dark: false,
      art: function (W, H, ns, dark, accent) {
        var c = dark ? FOIL : INK;
        var pitch = 0.30 * (H / 19.05), rnd = lcg(20260905), B = 5, i, y;
        var band = []; for (i = 0; i < B; i++) band[i] = '';
        var drag = '';
        for (y = pitch * 0.6; y < H; y += pitch) {
          var f = y / H;
          var spec = 1 -
            0.80 * Math.exp(-Math.pow((f - 0.285) / 0.075, 2)) -
            0.62 * Math.exp(-Math.pow((f - 0.735) / 0.062, 2));
          var v = (0.34 + 0.66 * rnd()) * Math.max(0, spec);
          if (v < 0.55) continue;
          var b = Math.min(B - 1, Math.max(0, Math.floor((v - 0.55) / 0.45 * B)));
          band[b] += M(0, y) + 'H' + r2(W);
          if (b >= B - 2 && rnd() > 0.66) drag += M(0, y) + 'H' + r2(W);
        }
        var o = [];
        for (i = 0; i < B; i++) if (band[i]) o.push(S(band[i], c, HAIR, 0.20 + i * 0.036));
        if (drag) o.push(S(drag, c, 0.15, 0.36));
        o.push(S(seg(0, H * 0.075, W, H * 0.075), c, BOLD, 0.30));
        o.push(S(seg(0, H * 0.972, W, H * 0.972), c, BOLD, 0.26));
        return o.join('');
      } },

/* ══ 14 · AIR MAIL ══════════════════════════════ DARK 1 of 2 ═══════════
   The most on-brief design in the field and the best-earned flood: a charcoal
   plate wearing the par-avion border, with the chevron stripes alternating
   FULL-STRENGTH BARE FOIL and the customer's accent — so the darkest ground
   in the set also carries the brightest windows, and it reads "send me home"
   from across a baggage hall.
   CUT SINCE REVIEW: both bands bled to the cut and a second accent pass hit
   the same edge, so normal die drift would have shown as ragged stripe ends
   label to label. Both bands are now FULLY INSET and clipped — the top one
   1.35 mm in from three cuts and stopping short of the header cap on both
   dies, the second one moved into the code-column pocket (x > 0.708 W,
   y 0.882-0.940 H) that both dies leave open. Nothing touches the die edge,
   nothing touches the keyline, and the postmark rings keep the plate from
   reading as two loose bars. */
    { key: 'airmail', name: 'Air Mail', dark: true, tint: '#16181d', op: 0.6,
      art: function (W, H, ns, dark, accent) {
        var a = acc(accent), o = [wash(W, H, CHAR, 1)], i;
        var tw = W * 0.022;
        /* band A — the masthead over the TEXT COLUMN ONLY. Starts at 1.10 mm,
           just clear of the keyline's inner edge at 1.09; stops at 0.122 H,
           just clear of the header cap on BOTH dies (0.134 std / 0.138 lg);
           and stops at the SEAM, because the QR caption sits at the top of the
           code column and on a dark ground that caption is KNOCKOUT FOIL — run
           full width, the band's own foil stripes swallowed "SCAN TO CALL"
           on both dies. Stopping at the seam also puts the two bright bands on
           a diagonal, which composes better than a pair of full-width rules. */
        var ax0 = 1.10, ax1 = W * SEAM, ay0 = 1.10, ay1 = H * 0.122;
        /* band B — the cancel block, in the one pocket both dies leave open:
           right of the reward bar, under the printed fallback, above the
           bottom keyline. */
        var bx0 = W * 0.708, bx1 = W - 1.10, by0 = H * 0.878, by1 = H * 0.940;
        o.push('<defs>' + clipDef(ns, 'bgA', ax0, ay0, ax1 - ax0, ay1 - ay0) +
                          clipDef(ns, 'bgB', bx0, by0, bx1 - bx0, by1 - by0) + '</defs>');
        o.push(clipped(ns, 'bgA',
          F(chevrons(ax0, ax1, ay0, ay1, tw, 0), FOIL, 1) +
          F(chevrons(ax0, ax1, ay0, ay1, tw, 1), a, 0.9)));
        o.push(clipped(ns, 'bgB',
          F(chevrons(bx0, bx1, by0, by1, tw, 0), FOIL, 1) +
          F(chevrons(bx0, bx1, by0, by1, tw, 1), a, 0.9)));
        /* the postmark: dashed rings behind the code, killer bars fading left */
        var cx = W * 0.805, cy = H * 0.46, Rr = H * 0.36;
        o.push(S(circ(cx, cy, Rr), FOIL, BOLD, 0.60, ' stroke-dasharray="1.0 0.62"'));
        o.push(S(circ(cx, cy, Rr * 0.74), FOIL, HAIR, 0.44, ' stroke-dasharray="0.6 0.5"'));
        var kb = '', kf = '';
        for (i = 0; i < 4; i++) {
          kb += waveD(W * 0.44, W * 0.80, cy + (i - 1.5) * H * 0.115, H * 0.020, W * 0.14, i % 2 === 0);
          kf += waveD(W * 0.16, W * 0.44, cy + (i - 1.5) * H * 0.115, H * 0.020, W * 0.14, i % 2 === 0);
        }
        o.push(S(kb, FOIL, RULE, 0.48));
        o.push(S(kf, FOIL, HAIR, 0.24));
        return o.join('');
      } },

/* ══ 15 · INK SLAB ══════════════════════════════ DARK 2 of 2 ═══════════
   The best-executed flood reviewed: a deep navy plate with the type knocked
   out to bare foil, and three 100% foil rails putting real stock back on the
   label — a full-width head, a spine up the left edge, and a foot UNDER THE
   CODE COLUMN ONLY. (Run full width, that foot fused with the also-bare-foil
   reward bar on the large die into one shapeless bright slab; stopped at the
   seam it reads as a deliberate return of the head rule.)
   CUT SINCE REVIEW: the rails were sized as fractions of the die — 0.048 H
   and 0.030 W — which lands at 0.91 mm on the standard die and 1.22 mm on the
   large, i.e. exactly ON the outer edge of the renderer's 1.0 mm keyline on
   one size and well inside it on the other. So the frame closed at the top on
   std and opened at the top on lg: the same design behaving differently at the
   two sizes. Every rail is now 1.30 mm in ABSOLUTE mm — wide enough to absorb
   the whole 0.18 mm keyline with 0.2 mm to spare on BOTH dies, and still 0.2 mm
   clear of the QR caption cap on std. The frame now opens at the head and the
   spine identically at both sizes, which is the look, deliberately.
   (A 0.85 mm rail was tried, to keep the keyline intact everywhere. It printed
   as a pinstripe and gave the plate back almost no stock — the exact "too much
   black" the brief exists to avoid. Better to lose the top of the keyline.)
   One accent stub is set into the head rail — the only place on a dark plate
   where the customer's colour can sit on bare foil rather than on ink. */
    { key: 'inkslab', name: 'Ink Slab', dark: true, tint: '#0b1730', op: 0.6,
      art: function (W, H, ns, dark, accent) {
        var s = W * SEAM;
        return wash(W, H, SLAB, 1) +
               R(0, 0, W, RAIL, FOIL) +
               R(0, 0, RAIL, H, FOIL) +
               R(s, H - RAIL, W - s, RAIL, FOIL) +
               R(W * 0.11, 0.18, W * 0.23, 0.60, acc(accent));
      } }

    ]
  };
})();
(function () {
  if (!window.LT || typeof window.LT.registerBgArt !== 'function') return;
  if (window.LT_BG) { try { window.LT.registerBgArt(window.LT_BG); } catch (e) {} }
  /* ── THE PICKER ORDER ─────────────────────────────────────────────────
     Owner, 2026-09-05: "the bgs suck compared to the Serial Seal defaults."
     So the artwork catalogue leads, in catalogue order and with the picker
     opening on the first of them; the bare stock (holo / silver / prismatic)
     comes straight after, because it is still the cheapest-looking-expensive
     thing this shop prints; then the shipped screens; then the drawn pack,
     ALL FIFTEEN of it still registered — a key is forever and a design record
     saved last week must still resolve to the ground it was bought with; then
     the two opaque plates last, exactly where the holo-first brief put them.
     Ranked through a map rather than indexOf-in-a-comparator: an unranked key
     (a page that registers a ground of its own) lands after everything known
     instead of tying with every other unranked key. */
  var i, ORDER = [];
  for (i = 0; i < window.LT.BGS.length; i++) {
    if (window.LT.BGS[i].kind === 'art') ORDER.push(window.LT.BGS[i].key);
  }
  ORDER = ORDER.concat(["holo", "silver", "prismatic",
    "blue", "red", "green", "purple", "gold",
    "tworule", "contour", "compass", "dotgrid", "aperture", "ridgeline",
    "duotone", "textpanel", "seachart", "meridian", "circuit", "anodize",
    "hairline", "airmail", "inkslab", "black", "premium"]);
  var rank = {};
  for (i = 0; i < ORDER.length; i++) rank[ORDER[i]] = i;
  window.LT.BGS.sort(function (a, b) {
    var x = rank[a.key], y = rank[b.key];
    return (x === undefined ? 9999 : x) - (y === undefined ? 9999 : y);
  });
})();
